import {
  getAvailableTimeSlots,
  getAvailableTimeSlotsByCareProviders,
  getAvailableTimeSlotsDetail,
  getAvailableTimeSlotsDetailByCareProviders,
} from 'api/services/Form';
import { getAvailableTimeSlotsDetailV2, getAvailableTimeSlotsV2 } from 'api/services/FormV2';
import CollapsableElement from 'components/CollapsableElement/CollapsableElement';
import ControlledCollapsable from 'components/ControlledCollapsable/ControlledCollapsable';
import Dropdown from 'components/Dropdown';
import InputField from 'components/InputField';
import Popup from 'components/Popup';
import CareProviderInfoPopup from 'components/Popup/CareProviderInfoPopup';
import VideoPopup from 'components/VideoPopup';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import moment from 'moment';
import { getServiceItemsWithProviders } from 'pages/Create/HomePage';
import { ChangeEvent, FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FORM_SECTION_LABELS, MAX_WIDTH_MOBILE } from 'utils/constant';
import {
  areAllTimeSlotsDisabledOrEmpty,
  formatDate,
  getAmcOnlineBookingUrl,
  getLocalizedValue,
  isSameMonthYear,
  resetDateToMidnight,
  tzStartDay,
} from 'utils/helper';
import {
  getAvailableTimeslotDisplayLabelV2,
  getFormOrderV2,
  getLocalizedValueV2,
  getSiteAddressTranslationKeyV2,
} from 'utils/helperV2';
import { ReactComponent as IconExpand } from '../../../assets/expand.svg';
import { ReactComponent as IconDone } from '../../../assets/done.svg';
import IconInfo from '../../../assets/icon_info.png';
import { ReactComponent as IconReminder } from '../../../assets/reminder.svg';
import { ReactComponent as IconVideo } from '../../../assets/video.svg';
import Button from '../../../components/Button';
import CollapsableBlock from '../../../components/CollapsableBlock';
import DatePicker from '../../../components/DatePicker';
import PatientForm from '../../../components/PatientForm';
import StepIndicator from '../../../components/StepIndicator';
import TimePickerV2 from '../../../components/TimePicker/v2';
import Tooltip from '../../../components/Tooltip';
import VerticalSelector from '../../../components/VerticalSelector';
import { IOption, POPUP_STATUS } from '../../../entities';
import {
  AvailableTimeslot,
  CareProvider,
  CareProviderAvailableTimeslot,
  Department,
  FORM_FLOW,
  FORM_SECTION,
  GetAvailableTimeSlotsRes,
  PatientFormValues,
  ServiceItem,
} from '../../../entities/Form';
import {
  AvailableTimeslotV2,
  BookingModeV2 as BookingMode,
  CareProviderAvailableTimeslotV2,
  FormValuesV2,
  SpecialtyFlowTypeV2,
  SpecialtyV2,
} from '../../../entities/FormV2';
import {
  validateEHealth,
  validateEmail,
  validateHKID,
  validateName,
  validatePhoneNumber,
} from '../../../utils/validator';
import baseStyles from './Form.module.scss';
import v2Styles from './v2.module.scss';
import {
  DoctorSpecialtyOptionV2 as DoctorSpecialtyOption,
  filterAggregatedSpecialtyOptionsV2,
  filterDoctorSpecialtyOptionsV2,
  getAggregatedSpecialtyOptionsV2,
  getDepartmentRemindersV2,
  getDoctorSpecialtyOptions,
  getEligibleCareProvidersV2,
  getMasterServiceItemOptions,
  getSpecialtyDoctorOptionsV2,
  getSpecialtyMasterServiceItemOptionsV2,
  mapDoctorTimeslotToFormValuesV2,
  matchesLocalizedNameV2,
  normalizeSearchValueV2,
} from '../HomePage/v2.utils';

const styles = { ...baseStyles, ...v2Styles };

export interface ServiceItemsWithProviders {
  serviceItem: ServiceItem;
  careProviders: CareProvider[];
}

const splitMap = {
  6: [3, 3],
  7: [4, 3],
  8: [4, 4],
  9: [5, 4],
};

const SPECIALTY_ANY_DOCTOR_VALUE = '__specialty_any_doctor__';

const isV2Mode = (mode?: BookingMode) => mode === BookingMode.Doctor || mode === BookingMode.Specialty;

const getServiceItemFromOption = (option: ServiceItem | ServiceItemsWithProviders) =>
  'serviceItem' in option ? option.serviceItem : option;

const isAvailableTimeslotV2 = (timeSlot?: AvailableTimeslot): timeSlot is AvailableTimeslotV2 =>
  !!timeSlot && 'department' in timeSlot && 'serviceItemHid' in timeSlot;

const getDoctorOptionLabel = (option: DoctorSpecialtyOption) =>
  `${option.careProvider.name} - ${getLocalizedValueV2(option.specialty) || option.specialty.nameEn}`;

// Google Material Symbols (Outlined, weight 300), sourced from the official material-design-icons repository:
// stethoscope, conditions, medical_services, work, and the digit paths from 24fps_select.
const MEDICAL_SERVICES_OUTLINE_PATH =
  'M172.31-100Q142-100 121-121q-21-21-21-51.31v-455.38Q100-658 121-679q21-21 51.31-21H340v-67.69Q340-798 361-819q21-21 51.31-21h135.38Q578-840 599-819q21 21 21 51.31V-700h167.69Q818-700 839-679q21 21 21 51.31v455.38Q860-142 839-121q-21 21-51.31 21H172.31Zm0-60h615.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H172.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM400-700h160v-67.69q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H412.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46V-700ZM160-160v-480 480Z';

const BookingModeIcon: FC<{ mode: BookingMode }> = ({ mode }) => {
  const commonProps = {
    width: 64,
    height: 64,
    viewBox: '0 -960 960 960',
    fill: 'currentColor',
    'aria-hidden': true,
  };

  switch (mode) {
    case BookingMode.Doctor:
      return (
        <svg {...commonProps}>
          <path d="M539.28-100q-99.87 0-170.15-70.04T298.85-340v-21.85q-81-10.53-135.5-72.23-54.5-61.69-54.5-145.92v-230h120v-40h60v140h-60v-40h-60v170q0 66 47 113t113 47q66 0 113-47t47-113v-170h-60v40h-60v-140h60v40h120v230q0 84.23-54.5 145.92-54.5 61.7-135.5 72.23V-340q0 75 52.61 127.5T539.23-160q75 0 127.5-52.5t52.5-127.5v-64.69q-31.54-9.31-51.92-36.27-20.39-26.96-20.39-61.35 0-42.62 29.86-72.46 29.85-29.84 72.5-29.84 42.64 0 72.45 29.84 29.81 29.84 29.81 72.46 0 34.39-20.39 61.35-20.38 26.96-51.92 36.27V-340q0 99.92-70.04 169.96T539.28-100Zm209.95-360q17.77 0 30.04-12.27t12.27-30.04q0-17.77-12.27-30.03-12.27-12.27-30.04-12.27t-30.04 12.27q-12.27 12.26-12.27 30.03 0 17.77 12.27 30.04T749.23-460Zm0-42.31Z" />
        </svg>
      );
    case BookingMode.Specialty:
      return (
        <svg {...commonProps}>
          <path d="M400-723.85q-30.46 0-52.15-21.69-21.7-21.69-21.7-52.15 0-30.46 21.7-52.16 21.69-21.69 52.15-21.69 30.46 0 52.15 21.69 21.7 21.7 21.7 52.16 0 30.46-21.7 52.15-21.69 21.69-52.15 21.69Zm260 490q44.31 0 75.23-30.92T766.15-340q0-44.31-30.92-75.23T660-446.15q-44.31 0-75.23 30.92T553.85-340q0 44.31 30.92 75.23T660-233.85ZM864-94.23 754.46-203.77q-21.23 14.77-44.98 22.35-23.75 7.57-49.48 7.57-69.23 0-117.69-48.46-48.46-48.46-48.46-117.69 0-69.23 48.46-117.69 48.46-48.46 117.69-48.46 69.23 0 117.69 48.46 48.46 48.46 48.46 117.69 0 25.73-7.57 49.48-7.58 23.75-22.35 44.98L905.77-136 864-94.23ZM443.85-90v-116.77q11.53 19 26.84 34.96t33.15 29.27V-90h-59.99Zm-147.69 0v-521.54q-59.85-5-118.89-13.73T60-649.23l14.62-60q80.53 21.08 161.34 29.38 80.8 8.31 164 8.31 83.19 0 164.04-8.31 80.85-8.3 161.38-29.38l14.62 60Q681.77-634 622.73-625.27t-118.89 13.73v74.08q-43.23 32.69-69.69 82.31-26.46 49.61-27.61 108.23v6.15q0 3.08.61 6.15h-51V-90h-59.99Z" />
        </svg>
      );
    case BookingMode.EKMC:
      return (
        <svg {...commonProps}>
          <path d={MEDICAL_SERVICES_OUTLINE_PATH} />
          <path d="M450-370v120h60v-120h120v-60H510v-120h-60v120H330v60h120Z" />
        </svg>
      );
    case BookingMode.BH:
      return (
        <svg {...commonProps}>
          <path d={MEDICAL_SERVICES_OUTLINE_PATH} />
          <g transform="translate(129.375 12.5) scale(.6875)">
            <path d="M215.39-420v-137.69q0-29.83 21.24-51.07Q257.87-630 287.69-630H360q5.39 0 8.85-3.46t3.46-8.85v-65.38q0-5.39-3.46-8.85T360-720H215.39v-60H360q29.83 0 51.07 21.24 21.24 21.24 21.24 51.07v65.38q0 29.83-21.24 51.07Q389.83-570 360-570h-72.31q-5.38 0-8.84 3.46-3.47 3.46-3.47 8.85V-480h152.31v60h-212.3Z" />
            <path d="M676.15-420v-110H527.69v-250h60v190h88.46v-190h60v190h68.46v60h-68.46v110h-60Z" />
          </g>
        </svg>
      );
  }
};

const getBookingModeSiteCode = (mode: BookingMode) => {
  if (mode === BookingMode.BH) return 'BH';
  if (mode === BookingMode.EKMC) return 'EKMC';
  return undefined;
};

const getBookingModeOptionThemeClass = (mode: BookingMode) => {
  if (mode === BookingMode.BH) return styles.bookingModeOptionBH;
  if (mode === BookingMode.EKMC) return styles.bookingModeOptionEKMC;
  return styles.bookingModeOptionClinical;
};

interface FormProps {
  mode: BookingMode | undefined;
  onSelectMode: (mode: BookingMode) => void;
  onBackToBookingMode: () => void;
  effectiveSiteId: string;

  flow: FORM_FLOW | undefined;

  specialtyOptions: SpecialtyV2[] | undefined;
  departmentOptions: Department[] | undefined;
  careProviderOptions: CareProvider[] | undefined;
  serviceItemOptions: ServiceItem[] | ServiceItemsWithProviders[] | undefined;

  formValues: FormValuesV2;
  onSubmit: (availableTimeSlotsDetail: AvailableTimeslot[]) => void;
  activeFormSection: FORM_SECTION;
  patientFormNoScroll?: boolean;
  onChangeFormValues: (values: FormValuesV2) => void;

  bookingTimeLabel: string | undefined;
  onSelectTime: (bookingTimeLabel?: string) => void;

  setIsFormValuesDirty: (isDirty: boolean) => void;
}

const Form: FC<PropsWithChildren<FormProps>> = ({
  mode,
  onSelectMode,
  onBackToBookingMode,
  effectiveSiteId,
  flow,
  specialtyOptions,
  departmentOptions,
  careProviderOptions,
  serviceItemOptions,
  formValues,
  onSubmit,
  activeFormSection: _activeFormSection,
  patientFormNoScroll,
  onChangeFormValues,
  bookingTimeLabel,
  onSelectTime,
  setIsFormValuesDirty,
}) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const [searchParams] = useSearchParams();
  const siteId = effectiveSiteId;
  const createBookingPath =
    pathname === '/ApptSysPublicWeb/internal-booking' ? '/ApptSysPublicWeb/internal-booking' : '/ApptSysPublicWeb';

  const careProvideTimeSlotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [scrollBarWideIndexes, setScrollBarWideIndexes] = useState<number[]>([]);

  const doctorOptions = useMemo(() => getDoctorSpecialtyOptions(specialtyOptions), [specialtyOptions]);
  const aggregatedSpecialtyOptions = useMemo(
    () => getAggregatedSpecialtyOptionsV2(specialtyOptions),
    [specialtyOptions],
  );
  const selectedSpecialtyOption = useMemo(
    () => aggregatedSpecialtyOptions?.find((option) => option.key === formValues.specialtyOptionKey),
    [aggregatedSpecialtyOptions, formValues.specialtyOptionKey],
  );
  const selectedSpecialtyFlowType = selectedSpecialtyOption?.specialties.some(
    (specialty) => specialty.flowType === SpecialtyFlowTypeV2.ServiceFirst,
  )
    ? SpecialtyFlowTypeV2.ServiceFirst
    : selectedSpecialtyOption?.specialty.flowType;
  const isSpecialtyServiceFirst =
    mode === BookingMode.Specialty && selectedSpecialtyFlowType === SpecialtyFlowTypeV2.ServiceFirst;
  const formOrder = getFormOrderV2(flow, mode, selectedSpecialtyFlowType);
  const allSpecialtyDoctorOptions = useMemo(
    () => getSpecialtyDoctorOptionsV2(selectedSpecialtyOption),
    [selectedSpecialtyOption],
  );
  const selectedDoctorOption = useMemo(
    () =>
      (formValues.preferredCareProviderHid || formValues.careProviderHid) && formValues.specialtyValue
        ? doctorOptions?.find(
            (option) =>
              option.careProvider.value === (formValues.preferredCareProviderHid || formValues.careProviderHid) &&
              option.specialty.value === formValues.specialtyValue,
          )
        : undefined,
    [doctorOptions, formValues.careProviderHid, formValues.preferredCareProviderHid, formValues.specialtyValue],
  );
  const doctorMasterServiceOption = mode === BookingMode.Doctor ? selectedDoctorOption : undefined;
  const specialtyMasterServiceOption = mode === BookingMode.Specialty ? selectedSpecialtyOption : undefined;
  const specialtyDoctorPreference = mode === BookingMode.Specialty ? formValues.doctorPreference : undefined;
  const specialtyPreferredCareProviderHid =
    specialtyDoctorPreference === 'specific' ? formValues.preferredCareProviderHid : undefined;
  const masterServiceItemOptions = useMemo(
    () =>
      mode === BookingMode.Doctor
        ? getMasterServiceItemOptions(doctorMasterServiceOption)
        : mode === BookingMode.Specialty
          ? isSpecialtyServiceFirst
            ? getSpecialtyMasterServiceItemOptionsV2(specialtyMasterServiceOption)
            : specialtyDoctorPreference
              ? getSpecialtyMasterServiceItemOptionsV2(specialtyMasterServiceOption, specialtyPreferredCareProviderHid)
              : []
          : [],
    [
      doctorMasterServiceOption,
      isSpecialtyServiceFirst,
      mode,
      specialtyDoctorPreference,
      specialtyMasterServiceOption,
      specialtyPreferredCareProviderHid,
    ],
  );
  const selectedMasterServiceItem = masterServiceItemOptions.find(
    (option) => option.masterServiceItem.hid === formValues.masterServiceItemHid,
  );
  const specialtyDoctorOptions = useMemo(() => {
    if (!isSpecialtyServiceFirst) return allSpecialtyDoctorOptions;
    if (!selectedMasterServiceItem) return [];

    const eligibleCareProviderHids = new Set(
      getEligibleCareProvidersV2(selectedMasterServiceItem).map((careProvider) => careProvider.value),
    );
    return allSpecialtyDoctorOptions.filter((option) => eligibleCareProviderHids.has(option.careProvider.value));
  }, [allSpecialtyDoctorOptions, isSpecialtyServiceFirst, selectedMasterServiceItem]);
  const selectedSpecialtyDoctorOption = specialtyDoctorOptions.find(
    (option) => option.careProvider.value === formValues.preferredCareProviderHid,
  );
  const selectedSpecialtyTimeslotDoctor = specialtyDoctorOptions.find(
    (option) => option.careProvider.value === formValues.careProviderHid,
  )?.careProvider;
  const selectedDepartment = departmentOptions?.find((opt) => opt.value === formValues.departmentValue);
  const selectedDoctor = careProviderOptions?.find((opt) => opt.value === formValues.careProviderHid);
  const selectedServiceItem = useMemo(
    () =>
      serviceItemOptions && serviceItemOptions[0]
        ? 'serviceItem' in serviceItemOptions[0]
          ? (serviceItemOptions as ServiceItemsWithProviders[]).find(
              (si) => si.serviceItem.value === formValues.serviceItemHid,
            )
          : (serviceItemOptions as ServiceItem[]).find((si) => si.value === formValues.serviceItemHid)
        : undefined,
    [serviceItemOptions, formValues.serviceItemHid],
  );

  const isSpecialtyAnyDoctor = mode === BookingMode.Specialty && formValues.doctorPreference === 'any';
  const isLegacyDepartmentLocked = mode === BookingMode.EKMC || mode === BookingMode.BH;
  const requestedDetailCareProviderHid = isV2Mode(mode)
    ? mode === BookingMode.Doctor
      ? formValues.preferredCareProviderHid || formValues.careProviderHid
      : formValues.preferredCareProviderHid
    : formValues.careProviderHid;
  const requestedDetailServiceItemHid = isV2Mode(mode) ? undefined : formValues.serviceItemHid;

  const onlyOneServiceItem = isV2Mode(mode)
    ? masterServiceItemOptions.length === 1
    : !!serviceItemOptions && serviceItemOptions.length === 1;
  const selectedSchedulingMinDate = isV2Mode(mode)
    ? selectedMasterServiceItem?.minDate
    : selectedServiceItem
      ? 'serviceItem' in selectedServiceItem
        ? selectedServiceItem.serviceItem.minDate
        : selectedServiceItem.minDate
      : undefined;
  const selectedSchedulingMaxDate = isV2Mode(mode)
    ? selectedMasterServiceItem?.maxDate
    : selectedServiceItem
      ? 'serviceItem' in selectedServiceItem
        ? selectedServiceItem.serviceItem.maxDate
        : selectedServiceItem.maxDate
      : undefined;

  const getSelectedLabels = (section: FORM_SECTION) => {
    switch (section) {
      case FORM_SECTION.DEPARTMENT:
        return [selectedDepartment?.name];
      case FORM_SECTION.SPECIALTY:
        return [getLocalizedValueV2(selectedSpecialtyOption?.specialty)];
      case FORM_SECTION.DOCTOR:
        return [
          mode === BookingMode.Doctor
            ? selectedDoctorOption
              ? getDoctorOptionLabel(selectedDoctorOption)
              : undefined
            : mode === BookingMode.Specialty
              ? formValues.doctorPreference === 'any'
                ? selectedSpecialtyTimeslotDoctor
                  ? t('allDoctorsOrCareProvidersSelected', {
                      doctor: selectedSpecialtyTimeslotDoctor.name,
                    })
                  : t('allDoctorsOrCareProviders')
                : selectedSpecialtyDoctorOption?.careProvider.name
              : selectedDoctor?.name,
        ];
      case FORM_SECTION.SERVICE:
        return [
          isV2Mode(mode)
            ? getLocalizedValueV2(selectedMasterServiceItem?.masterServiceItem)
            : selectedServiceItem
              ? getLocalizedValue({
                  nameSc:
                    'serviceItem' in selectedServiceItem
                      ? selectedServiceItem.serviceItem.nameSc
                      : selectedServiceItem.nameSc,
                  nameTc:
                    'serviceItem' in selectedServiceItem
                      ? selectedServiceItem.serviceItem.nameTc
                      : selectedServiceItem.nameTc,
                  name:
                    'serviceItem' in selectedServiceItem
                      ? selectedServiceItem.serviceItem.name
                      : selectedServiceItem.name,
                })
              : undefined,
        ];
      case FORM_SECTION.DATE_TIME:
        return [bookingTimeLabel];
      case FORM_SECTION.PATIENT_FORM:
        return [
          formValues.lastName && formValues.firstName ? `${formValues.lastName} ${formValues.firstName}` : undefined,
          formValues.countryCode && formValues.mobileNo
            ? `(${formValues.countryCode}) ${formValues.mobileNo}`
            : undefined,
        ];
      default:
        return [];
    }
  };
  const getEditable = (section: FORM_SECTION) => {
    switch (section) {
      case FORM_SECTION.DEPARTMENT:
        return !isLegacyDepartmentLocked && !!departmentOptions;
      case FORM_SECTION.SPECIALTY:
        return !!aggregatedSpecialtyOptions?.length;
      case FORM_SECTION.DOCTOR:
        return mode === BookingMode.Doctor
          ? !!selectedDoctorOption
          : mode === BookingMode.Specialty
            ? !!formValues.doctorPreference && !!selectedSpecialtyOption
            : !!formValues?.departmentValue && !!careProviderOptions && careProviderOptions.length > 1;
      case FORM_SECTION.SERVICE:
        if (isV2Mode(mode)) {
          const hasRequiredPriorSelection =
            mode === BookingMode.Doctor
              ? !!selectedDoctorOption
              : isSpecialtyServiceFirst
                ? !!selectedSpecialtyOption
                : !!formValues.doctorPreference;
          return hasRequiredPriorSelection && masterServiceItemOptions.length > 1;
        }
        return (
          (flow === FORM_FLOW.DEFAULT ? !!formValues?.careProviderHid : !!formValues?.departmentValue) &&
          !!serviceItemOptions &&
          serviceItemOptions.length > 1
        );
      case FORM_SECTION.DATE_TIME:
        return isV2Mode(mode)
          ? !!formValues.masterServiceItemHid && (!isSpecialtyServiceFirst || !!formValues.doctorPreference)
          : !!formValues?.serviceItemHid;
      case FORM_SECTION.PATIENT_FORM:
        return (
          !!formValues?.fromDt ||
          !!formValues?.lastName ||
          !!formValues?.firstName ||
          !!formValues?.mobileNo ||
          !!formValues?.email ||
          !!formValues?.isJoinedEhealth ||
          !!formValues?.remarks ||
          !!formValues?.idNo
        );
      default:
        return false;
    }
  };

  const [activeFormSection, setActiveFormSection] = useState<FORM_SECTION>(_activeFormSection);
  const renderedFormOrder =
    flow === FORM_FLOW.DOCTOR_LAST
      ? formOrder.filter((section) => !(onlyOneServiceItem && section === FORM_SECTION.SERVICE))
      : formOrder;
  const activeFormSectionIndex = renderedFormOrder.indexOf(activeFormSection);
  const visibleFormOrder =
    activeFormSection === FORM_SECTION.NULL || activeFormSectionIndex < 0
      ? renderedFormOrder
      : renderedFormOrder.slice(0, activeFormSectionIndex + 1);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<GetAvailableTimeSlotsRes | null>();
  const [availableTimeSlotsDetail, setAvailableTimeSlotsDetail] = useState<AvailableTimeslot[]>();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [submitted, setSubmitted] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<GetAvailableTimeSlotsRes | null>();
  const [selectedTimeSlotsDetail, setSelectedTimeSlotsDetail] = useState<AvailableTimeslot[]>();
  const [availableCareProviderTimeSlotsDetail, setAvailableCareProviderTimeSlotsDetail] =
    useState<CareProviderAvailableTimeslot[]>();
  const [selectedCareProviderTimeSlotsDetail, setSelectedCareProviderTimeSlotsDetail] =
    useState<CareProviderAvailableTimeslot[]>();
  const [availableV2CareProviderTimeSlotsDetail, setAvailableV2CareProviderTimeSlotsDetail] =
    useState<CareProviderAvailableTimeslotV2[]>();
  const [selectedV2CareProviderTimeSlotsDetail, setSelectedV2CareProviderTimeSlotsDetail] =
    useState<CareProviderAvailableTimeslotV2[]>();
  const detailRequestIdRef = useRef(0);
  const timeSlotsRequestIdRef = useRef(0);
  const [filterCareProvider, setFilterCareProvider] = useState('');
  const [specialtyNameFilter, setSpecialtyNameFilter] = useState('');
  const [doctorNameFilter, setDoctorNameFilter] = useState('');
  const [serviceItemNameFilter, setServiceItemNameFilter] = useState('');
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  const filteredDoctorOptions = useMemo(
    () => filterDoctorSpecialtyOptionsV2(doctorOptions, doctorNameFilter),
    [doctorNameFilter, doctorOptions],
  );

  const filteredAggregatedSpecialtyOptions = useMemo(
    () => filterAggregatedSpecialtyOptionsV2(aggregatedSpecialtyOptions, specialtyNameFilter),
    [aggregatedSpecialtyOptions, specialtyNameFilter],
  );

  const filteredSpecialtyDoctorOptions = useMemo(
    () =>
      specialtyDoctorOptions.filter((option) =>
        matchesLocalizedNameV2(doctorNameFilter, [
          option.careProvider.name,
          option.careProvider.nameEn,
          option.careProvider.nameTc,
          option.careProvider.nameSc,
        ]),
      ),
    [doctorNameFilter, specialtyDoctorOptions],
  );

  const filteredMasterServiceItemOptions = useMemo(
    () =>
      masterServiceItemOptions.filter((option) =>
        matchesLocalizedNameV2(serviceItemNameFilter, [
          option.masterServiceItem.nameEn,
          option.masterServiceItem.nameTc,
          option.masterServiceItem.nameSc,
        ]),
      ),
    [masterServiceItemOptions, serviceItemNameFilter],
  );

  const filteredCareProviderOptions = useMemo(
    () => careProviderOptions?.filter((careProvider) => matchesLocalizedNameV2(doctorNameFilter, [careProvider.name])),
    [careProviderOptions, doctorNameFilter],
  );

  const filteredServiceItemOptions = useMemo(
    () =>
      (serviceItemOptions as Array<ServiceItem | ServiceItemsWithProviders> | undefined)?.filter((option) => {
        const serviceItem = getServiceItemFromOption(option);
        return matchesLocalizedNameV2(serviceItemNameFilter, [
          serviceItem.name,
          serviceItem.nameTc,
          serviceItem.nameSc,
        ]);
      }),
    [serviceItemNameFilter, serviceItemOptions],
  );

  const doctorSelectionOptions =
    mode === BookingMode.Doctor
      ? doctorOptions
      : mode === BookingMode.Specialty
        ? selectedSpecialtyOption
          ? specialtyDoctorOptions
          : undefined
        : careProviderOptions;

  const handleVideoClick = (url: string) => {
    setSelectedVideoUrl(url);
  };

  const handleCloseVideo = () => {
    setSelectedVideoUrl(null);
  };

  const datePickerRef = useRef<ReactDatePicker>(null);

  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();

  const [confirmEditPopupSection, setConfirmEditPopupSection] = useState<FORM_SECTION>();
  const [confirmEditValue, setConfirmEditValue] = useState('');
  const [pendingSectionEdit, setPendingSectionEdit] = useState<FORM_SECTION>();
  const confirmedSectionEditRef = useRef<FORM_SECTION>();
  const [isBackToBookingModePopupOpen, setIsBackToBookingModePopupOpen] = useState(false);
  const hasSelectedValues = Object.entries(formValues).some(([key, value]) =>
    key === 'countryCode'
      ? value !== undefined && value !== '' && value !== '852'
      : value !== undefined && value !== '',
  );

  const hasRelatedSelections = (section: FORM_SECTION) => {
    switch (section) {
      case FORM_SECTION.SPECIALTY:
        return !!(
          formValues.doctorPreference ||
          formValues.preferredCareProviderHid ||
          formValues.careProviderHid ||
          formValues.masterServiceItemHid ||
          formValues.serviceItemHid ||
          formValues.fromDt ||
          formValues.toDt
        );
      case FORM_SECTION.DEPARTMENT:
        return !!(formValues.careProviderHid || formValues.serviceItemHid || formValues.fromDt || formValues.toDt);
      case FORM_SECTION.DOCTOR:
        return isSpecialtyServiceFirst
          ? !!(formValues.fromDt || formValues.toDt)
          : !!(formValues.masterServiceItemHid || formValues.serviceItemHid || formValues.fromDt || formValues.toDt);
      case FORM_SECTION.SERVICE:
        return isSpecialtyServiceFirst
          ? !!(
              formValues.doctorPreference ||
              formValues.preferredCareProviderHid ||
              formValues.careProviderHid ||
              formValues.fromDt ||
              formValues.toDt
            )
          : !!(formValues.fromDt || formValues.toDt);
      default:
        return false;
    }
  };

  const handleSectionEdit = (section: FORM_SECTION) => {
    confirmedSectionEditRef.current = undefined;
    if (hasRelatedSelections(section)) {
      setPendingSectionEdit(section);
      return;
    }

    setActiveFormSection(section);
  };

  const handleBackToBookingMode = () => {
    if (hasSelectedValues) {
      setIsBackToBookingModePopupOpen(true);
      return;
    }

    onBackToBookingMode();
  };

  const handleSelectSpecialtyDoctor = () => {
    confirmedSectionEditRef.current = FORM_SECTION.DOCTOR;
    setActiveFormSection(FORM_SECTION.DOCTOR);
  };

  const [isCareProviderInfoPopupOpen, setCareProviderInfoPopupIsOpen] = useState(false);
  const [selectedCareProviderHid, setSelectedCareProviderHid] = useState<string>();

  const selectedCareProviderInfo =
    mode === BookingMode.Doctor
      ? (() => {
          const careProvider = doctorOptions?.find(
            (option) => option.careProvider.value === selectedCareProviderHid,
          )?.careProvider;
          return careProvider
            ? {
                ...careProvider,
                name: careProvider.name,
              }
            : undefined;
        })()
      : mode === BookingMode.Specialty
        ? specialtyDoctorOptions.find((option) => option.careProvider.value === selectedCareProviderHid)?.careProvider
        : careProviderOptions?.find((option) => option.value === selectedCareProviderHid);

  const [patientFormErrors, setPatientFormErrors] = useState<PatientFormValues>({
    lastName: undefined,
    firstName: undefined,
    idNo: undefined,
    mobileNo: undefined,
    email: undefined,
    isJoinedEhealth: undefined,
    remarks: undefined,
  });

  const resetFormStates = () => {
    detailRequestIdRef.current += 1;
    timeSlotsRequestIdRef.current += 1;
    setSelectedDate(undefined);
    setSelectedTimeSlot(undefined);
    // setFilterCareProvider("");
    setAvailableTimeSlots(undefined);
    setAvailableTimeSlotsDetail(undefined);
    setAvailableCareProviderTimeSlotsDetail(undefined);
    setAvailableV2CareProviderTimeSlotsDetail(undefined);
  };

  const onChangeDepartmentValue = (departmentValue: string, searchParam?: boolean) => {
    confirmedSectionEditRef.current = undefined;
    if (!searchParam) setIsFormValuesDirty(true);
    const newFlow = departmentOptions?.find((opt) => opt.value === departmentValue)?.flow;

    const department = departmentOptions?.find((opt) => opt.value === departmentValue);
    const careProvider = searchParam
      ? department?.careProviders?.find((cp) => cp.code === searchParams.get('cp'))
      : undefined;
    const careProviderSearchParamValue = careProvider?.value || undefined;
    const serviceItemSearchParamValue =
      newFlow === FORM_FLOW.DEFAULT
        ? careProvider?.serviceItems?.find((si) => si.value === searchParams.get('si'))?.value || undefined
        : getServiceItemsWithProviders(department)?.find((si) => si.serviceItem.value === searchParams.get('si'))
            ?.serviceItem?.value || undefined;

    const _careProviderOptions = department?.careProviders;
    const _onlyOneCareProvider = _careProviderOptions?.length === 1;

    const _serviceItemOptions =
      newFlow === FORM_FLOW.DEFAULT
        ? _onlyOneCareProvider
          ? department?.careProviders?.[0].serviceItems
          : department?.careProviders?.find((cp) => cp.value === careProviderSearchParamValue)?.serviceItems
        : getServiceItemsWithProviders(department);

    const _onlyOneServiceItem = _serviceItemOptions?.length === 1;

    resetFormStates();

    onSelectTime(undefined);
    setDoctorNameFilter('');
    setServiceItemNameFilter('');
    onChangeFormValues({
      ...formValues,
      departmentValue,
      careProviderHid:
        newFlow === FORM_FLOW.DEFAULT && _onlyOneCareProvider
          ? _careProviderOptions[0].value
          : searchParam && careProviderSearchParamValue && newFlow === FORM_FLOW.DEFAULT
            ? careProviderSearchParamValue
            : undefined,
      serviceItemHid:
        (newFlow === FORM_FLOW.DEFAULT ? _onlyOneCareProvider : true) && _onlyOneServiceItem
          ? 'serviceItem' in _serviceItemOptions[0]
            ? _serviceItemOptions[0].serviceItem.value
            : _serviceItemOptions[0].value
          : serviceItemSearchParamValue
            ? serviceItemSearchParamValue
            : undefined,
      fromDt: undefined,
    });

    setFilterCareProvider(
      careProviderSearchParamValue &&
        newFlow === FORM_FLOW.DOCTOR_LAST &&
        (_serviceItemOptions?.length === 1 || serviceItemSearchParamValue)
        ? careProviderSearchParamValue
        : '',
    );

    setActiveFormSection(
      newFlow === FORM_FLOW.DEFAULT
        ? _onlyOneCareProvider
          ? _onlyOneServiceItem
            ? FORM_SECTION.DATE_TIME
            : FORM_SECTION.SERVICE
          : careProviderSearchParamValue
            ? serviceItemSearchParamValue
              ? FORM_SECTION.DATE_TIME
              : FORM_SECTION.SERVICE
            : FORM_SECTION.DOCTOR
        : _onlyOneServiceItem
          ? FORM_SECTION.DATE_TIME
          : serviceItemSearchParamValue
            ? FORM_SECTION.DATE_TIME
            : FORM_SECTION.SERVICE,
    );
  };

  const onChangeTimeSlot = (
    fromDt: string,
    toDt: string,
    careProviderHid?: string,
    selectedTimeslotDetail?: AvailableTimeslot,
  ) => {
    if (isV2Mode(mode) && !isAvailableTimeslotV2(selectedTimeslotDetail)) return;

    setIsFormValuesDirty(true);
    const selectedDate = new Date(new Date(fromDt).setHours(0, 0, 0, 0));

    const displayingTimeSlotsDateKey = availableTimeSlots?.timeSlotDictionary
      ? Object.keys(availableTimeSlots.timeSlotDictionary)?.[0]
      : undefined;

    const displayingTimeSlotsDate = resetDateToMidnight(displayingTimeSlotsDateKey);

    if (displayingTimeSlotsDate && isSameMonthYear(displayingTimeSlotsDate, selectedDate))
      setSelectedTimeSlot(availableTimeSlots);
    else {
      const availableTimeSlotsDateKey = selectedTimeSlot?.timeSlotDictionary
        ? Object.keys(selectedTimeSlot.timeSlotDictionary)?.[0]
        : undefined;

      const availableTimeSlotsDate = resetDateToMidnight(availableTimeSlotsDateKey);

      if (!availableTimeSlotsDate || !isSameMonthYear(availableTimeSlotsDate, selectedDate)) {
        flow === FORM_FLOW.DOCTOR_LAST ? getTimeSlotsByCareProviders(selectedDate) : getTimeSlots(selectedDate);
      }
    }

    if (isV2Mode(mode) && isAvailableTimeslotV2(selectedTimeslotDetail)) {
      if (isSpecialtyAnyDoctor) {
        setSelectedV2CareProviderTimeSlotsDetail(availableV2CareProviderTimeSlotsDetail);
      } else {
        setSelectedTimeSlotsDetail(availableTimeSlotsDetail);
      }
      onSelectTime(
        `${formatDate(fromDt)} ${getAvailableTimeslotDisplayLabelV2(selectedTimeslotDetail, i18n.language)}`,
      );
    } else if (flow === FORM_FLOW.DOCTOR_LAST) {
      setSelectedCareProviderTimeSlotsDetail(availableCareProviderTimeSlotsDetail);
      const selectedAvailableCareProviderTimeSlots = availableCareProviderTimeSlotsDetail?.find(
        (careProviderTimeSlot) => careProviderTimeSlot.careProviderHid === careProviderHid,
      )?.availableTimeslots;
      onSelectTime(
        `${formatDate(fromDt!)} ${
          i18n.language === 'zh-CN'
            ? selectedAvailableCareProviderTimeSlots?.find((timeSlot) => timeSlot.fromDt === fromDt)?.labelSc
            : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
              ? selectedAvailableCareProviderTimeSlots?.find((timeSlot) => timeSlot.fromDt === fromDt)?.labelTc
              : selectedAvailableCareProviderTimeSlots?.find((timeSlot) => timeSlot.fromDt === fromDt)?.label
        } ${careProviderOptions?.find((careProvider) => careProvider.value === careProviderHid)?.name}`,
      );
    } else {
      setSelectedTimeSlotsDetail(availableTimeSlotsDetail);
      const timeSlot =
        selectedTimeslotDetail ||
        availableTimeSlotsDetail?.find((option) => option.fromDt === fromDt && option.toDt === toDt);
      const timeSlotLabel = isAvailableTimeslotV2(timeSlot)
        ? getAvailableTimeslotDisplayLabelV2(timeSlot, i18n.language)
        : i18n.language === 'zh-CN'
          ? timeSlot?.labelSc
          : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
            ? timeSlot?.labelTc
            : timeSlot?.label;
      onSelectTime(`${formatDate(fromDt)} ${timeSlotLabel}`);
    }
    onChangeFormValues(
      isV2Mode(mode) && isAvailableTimeslotV2(selectedTimeslotDetail)
        ? mapDoctorTimeslotToFormValuesV2(
            formValues,
            selectedTimeslotDetail,
            selectedMasterServiceItem?.sources,
            careProviderHid || formValues.preferredCareProviderHid || formValues.careProviderHid,
          )
        : {
            ...formValues,
            fromDt,
            toDt,
            careProviderHid: flow === FORM_FLOW.DOCTOR_LAST ? careProviderHid : formValues.careProviderHid,
          },
    );
    setActiveFormSection(!isPatientFormRequiredFilled ? FORM_SECTION.PATIENT_FORM : FORM_SECTION.NULL);
  };

  useEffect(() => {
    if (mode === BookingMode.Doctor && !formValues.departmentValue) {
      setActiveFormSection(FORM_SECTION.DOCTOR);
    } else if (mode === BookingMode.Specialty && !formValues.specialtyOptionKey) {
      setActiveFormSection(FORM_SECTION.SPECIALTY);
    }
  }, [mode]);

  useEffect(() => {
    if (isLegacyDepartmentLocked && formValues.departmentValue && activeFormSection === FORM_SECTION.DEPARTMENT) {
      setActiveFormSection(flow === FORM_FLOW.DOCTOR_LAST ? FORM_SECTION.SERVICE : FORM_SECTION.DOCTOR);
    }
  }, [activeFormSection, flow, formValues.departmentValue, isLegacyDepartmentLocked]);

  useEffect(() => {
    if (!departmentOptions) return;
    if (isV2Mode(mode)) return;
    const location = searchParams.get('location')
      ? departmentOptions?.find((opt) => opt.value === searchParams.get('location'))?.value
      : undefined;

    if (departmentOptions.length > 0 && !formValues.departmentValue && departmentOptions.length <= 1)
      onChangeDepartmentValue(departmentOptions[0]?.value, !!location);
    else if (location && !formValues.departmentValue) onChangeDepartmentValue(location, true);
  }, [departmentOptions, searchParams]);

  useEffect(() => {
    if (isV2Mode(mode)) return;
    if (
      flow === FORM_FLOW.DEFAULT &&
      careProviderOptions &&
      careProviderOptions.length > 0 &&
      careProviderOptions.length <= 1 &&
      !formValues.careProviderHid
    )
      onChangeCareProviderHid(careProviderOptions[0]?.value);
  }, [careProviderOptions, formValues]);

  useEffect(() => {
    if (isV2Mode(mode)) {
      if (masterServiceItemOptions.length === 1 && !formValues.masterServiceItemHid) {
        onChangeServiceItemHid(masterServiceItemOptions[0].masterServiceItem.hid);
      }
      return;
    }

    if (
      serviceItemOptions &&
      serviceItemOptions.length > 0 &&
      serviceItemOptions.length <= 1 &&
      !formValues.serviceItemHid
    )
      onChangeServiceItemHid(
        'serviceItem' in serviceItemOptions[0] ? serviceItemOptions[0].serviceItem.value : serviceItemOptions[0].value,
      );
  }, [serviceItemOptions, masterServiceItemOptions, mode, formValues]);

  const onChangeSpecialtyOptionKey = (specialtyOptionKey: string) => {
    confirmedSectionEditRef.current = undefined;
    const specialtyOption = aggregatedSpecialtyOptions?.find((option) => option.key === specialtyOptionKey);
    if (!specialtyOption) return;

    setIsFormValuesDirty(true);
    resetFormStates();
    onSelectTime(undefined);
    setDoctorNameFilter('');
    setServiceItemNameFilter('');
    const specialtyMasterServiceItems = getSpecialtyMasterServiceItemOptionsV2(specialtyOption);
    const defaultMasterServiceItemHid =
      specialtyMasterServiceItems.length === 1 ? specialtyMasterServiceItems[0].masterServiceItem.hid : undefined;
    const usesServiceFirstFlow = specialtyOption.specialties.some(
      (specialty) => specialty.flowType === SpecialtyFlowTypeV2.ServiceFirst,
    );
    onChangeFormValues({
      ...formValues,
      departmentValue: undefined,
      departmentHid: undefined,
      specialtyHid: specialtyOption.specialty.hid,
      specialtyValue: specialtyOption.specialty.value,
      specialtyOptionKey,
      doctorPreference: usesServiceFirstFlow ? undefined : 'any',
      preferredCareProviderHid: undefined,
      careProviderHid: undefined,
      masterServiceItemHid: defaultMasterServiceItemHid,
      serviceItemHid: undefined,
      fromDt: undefined,
      toDt: undefined,
    });
    setActiveFormSection(
      usesServiceFirstFlow
        ? defaultMasterServiceItemHid
          ? FORM_SECTION.DOCTOR
          : FORM_SECTION.SERVICE
        : defaultMasterServiceItemHid
          ? FORM_SECTION.DATE_TIME
          : FORM_SECTION.SERVICE,
    );
  };

  const onChangeCareProviderHid = (careProviderHid: string) => {
    confirmedSectionEditRef.current = undefined;
    setIsFormValuesDirty(true);
    resetFormStates();
    onSelectTime(undefined);
    setDoctorNameFilter('');
    setServiceItemNameFilter('');

    if (mode === BookingMode.Doctor) {
      const doctorOption = doctorOptions?.find((option) => option.key === careProviderHid);
      if (!doctorOption) return;

      onChangeFormValues({
        ...formValues,
        departmentValue: undefined,
        departmentHid: undefined,
        specialtyHid: doctorOption.specialty.hid,
        specialtyValue: doctorOption.specialty.value,
        specialtyOptionKey: undefined,
        doctorPreference: 'specific',
        preferredCareProviderHid: doctorOption.careProvider.value,
        masterServiceItemHid: undefined,
        careProviderHid: doctorOption.careProvider.value,
        serviceItemHid: undefined,
        fromDt: undefined,
        toDt: undefined,
      });
      setActiveFormSection(FORM_SECTION.SERVICE);
      return;
    }

    if (mode === BookingMode.Specialty) {
      const isAnyDoctor = careProviderHid === SPECIALTY_ANY_DOCTOR_VALUE;
      const preferredCareProviderHid = isAnyDoctor ? undefined : careProviderHid;

      onChangeFormValues({
        ...formValues,
        departmentValue: undefined,
        departmentHid: undefined,
        doctorPreference: isAnyDoctor ? 'any' : 'specific',
        preferredCareProviderHid,
        careProviderHid: undefined,
        masterServiceItemHid: isSpecialtyServiceFirst ? formValues.masterServiceItemHid : undefined,
        serviceItemHid: undefined,
        fromDt: undefined,
        toDt: undefined,
      });
      setActiveFormSection(isSpecialtyServiceFirst ? FORM_SECTION.DATE_TIME : FORM_SECTION.SERVICE);
      return;
    }

    onChangeFormValues({
      ...formValues,
      doctorPreference: undefined,
      preferredCareProviderHid: undefined,
      careProviderHid,
      serviceItemHid:
        careProviderHid === formValues.careProviderHid &&
        serviceItemOptions &&
        serviceItemOptions.length > 0 &&
        serviceItemOptions.length === 1
          ? 'serviceItem' in serviceItemOptions[0]
            ? serviceItemOptions[0].serviceItem.value
            : serviceItemOptions[0].value
          : undefined,
      fromDt: undefined,
      toDt: undefined,
    });
    setActiveFormSection(
      careProviderHid === formValues.careProviderHid && serviceItemOptions && serviceItemOptions.length === 1
        ? FORM_SECTION.DATE_TIME
        : FORM_SECTION.SERVICE,
    );
  };

  const onChangeServiceItemHid = (serviceItemHid: string) => {
    confirmedSectionEditRef.current = undefined;
    setIsFormValuesDirty(true);
    resetFormStates();
    onSelectTime(undefined);
    setServiceItemNameFilter('');
    if (isSpecialtyServiceFirst) setDoctorNameFilter('');
    onChangeFormValues(
      isV2Mode(mode)
        ? {
            ...formValues,
            departmentValue: undefined,
            departmentHid: undefined,
            doctorPreference:
              mode === BookingMode.Specialty && isSpecialtyServiceFirst ? undefined : formValues.doctorPreference,
            preferredCareProviderHid:
              mode === BookingMode.Specialty && isSpecialtyServiceFirst
                ? undefined
                : formValues.preferredCareProviderHid,
            masterServiceItemHid: serviceItemHid,
            careProviderHid:
              mode === BookingMode.Doctor
                ? formValues.preferredCareProviderHid || formValues.careProviderHid
                : undefined,
            serviceItemHid: undefined,
            fromDt: undefined,
            toDt: undefined,
          }
        : {
            ...formValues,
            serviceItemHid,
            careProviderHid: flow === FORM_FLOW.DEFAULT ? formValues.careProviderHid : undefined,
            fromDt: undefined,
          },
    );
    setActiveFormSection(isSpecialtyServiceFirst ? FORM_SECTION.DOCTOR : FORM_SECTION.DATE_TIME);
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'firstName':
        return value ? validateName(value) : undefined;
      case 'lastName':
        return validateName(value);
      case 'idNo':
        return validateHKID(value);
      case 'mobileNo':
        return validatePhoneNumber(formValues.countryCode, value);
      case 'email':
        return !value ? undefined : validateEmail(value);
      case 'isJoinedEhealth':
        return validateEHealth(value);
      default:
        return undefined;
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | IOption) => {
    setIsFormValuesDirty(true);
    let name: string;
    let value: string;

    if ('target' in e) ({ name, value } = e.target);
    else ({ name, value } = e);

    onChangeFormValues({
      ...formValues,
      [name]: value,
    });

    if (submitted)
      setPatientFormErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
  };

  const validateAllFields = () => {
    const errors = {
      lastName: validateName(formValues.lastName),
      firstName: formValues.firstName ? validateName(formValues.firstName) : undefined,
      idNo: validateHKID(formValues.idNo),
      mobileNo: validatePhoneNumber(formValues.countryCode, formValues.mobileNo),
      email: !formValues.email ? undefined : validateEmail(formValues.email),
      isJoinedEhealth: validateEHealth(formValues.isJoinedEhealth),
    };
    setPatientFormErrors(errors);

    if (Object.values(errors).some((error) => error !== undefined)) {
      activeFormSection !== FORM_SECTION.PATIENT_FORM && setActiveFormSection(FORM_SECTION.PATIENT_FORM);
      const firstErrorName = Object.entries(errors).find(([key, value]) => value !== undefined)?.[0] || null;
      const firstErrorElement = firstErrorName ? document.getElementsByName(firstErrorName)[0] : null;
      if (firstErrorElement) {
        setTimeout(
          () => {
            firstErrorElement.focus({ preventScroll: true });
            window.scrollTo({
              top: firstErrorElement.getBoundingClientRect().top + window.scrollY - 46,
              behavior: 'smooth',
            });
          },
          activeFormSection !== FORM_SECTION.PATIENT_FORM ? 750 : 0,
        );
      }
    }
    return !Object.values(errors).some((error) => error !== undefined);
  };

  const isPatientFormRequiredFilled =
    !!formValues.lastName &&
    !!formValues.firstName &&
    // !!formValues.idNo &&
    !!formValues.mobileNo &&
    !!formValues.isJoinedEhealth;

  const isAllRequiredFilled =
    !!formValues.serviceItemHid &&
    !!formValues.careProviderHid &&
    !!formValues.fromDt &&
    !!formValues.toDt &&
    (!isV2Mode(mode) ||
      (!!formValues.masterServiceItemHid &&
        !!formValues.departmentHid &&
        (mode !== BookingMode.Specialty || (!!formValues.specialtyOptionKey && !!formValues.doctorPreference))));

  const timeSlotSubGroups = Object.values(availableTimeSlots?.timeSlotDictionary || {}).flatMap(
    ({ subGroups }) => subGroups,
  );
  const isFullyBooked =
    isV2Mode(mode) && timeSlotSubGroups.length > 0 && timeSlotSubGroups.every(({ roomSlot }) => roomSlot === 0);
  const datePlaceholder = t(isFullyBooked ? 'allAppointmentSlotsFullyBooked' : 'pleaseSelectDate');

  const noTimeSlot =
    availableTimeSlots &&
    selectedSchedulingMinDate &&
    areAllTimeSlotsDisabledOrEmpty(availableTimeSlots.timeSlotDictionary) &&
    isSameMonthYear(new Date(availableTimeSlots.minDate), new Date(availableTimeSlots.maxDate));

  const v2CareProviderTimeSlotGroups = availableV2CareProviderTimeSlotsDetail?.filter(
    (group) => group.availableTimeslots.length > 0,
  );

  const isTimeSlotDetailLoading =
    !!selectedDate &&
    (isSpecialtyAnyDoctor
      ? availableV2CareProviderTimeSlotsDetail === undefined
      : flow === FORM_FLOW.DOCTOR_LAST
        ? availableCareProviderTimeSlotsDetail === undefined
        : !!selectedSchedulingMinDate && availableTimeSlotsDetail === undefined);

  const departmentReminders = useMemo(() => {
    if (!isV2Mode(mode)) return selectedDepartment?.notes ? [selectedDepartment.notes] : [];
    if (!selectedDate || !selectedMasterServiceItem) return [];

    const timeSlotGroups = isSpecialtyAnyDoctor
      ? (availableV2CareProviderTimeSlotsDetail || []).map((group) => ({
          careProviderHid: group.careProvider.value,
          availableTimeslots: group.availableTimeslots,
        }))
      : [
          {
            careProviderHid: requestedDetailCareProviderHid,
            availableTimeslots: availableTimeSlotsDetail?.filter(isAvailableTimeslotV2) || [],
          },
        ];

    return getDepartmentRemindersV2(timeSlotGroups, selectedMasterServiceItem.sources, i18n.language);
  }, [
    availableTimeSlotsDetail,
    availableV2CareProviderTimeSlotsDetail,
    i18n.language,
    isSpecialtyAnyDoctor,
    mode,
    requestedDetailCareProviderHid,
    selectedDate,
    selectedDepartment?.notes,
    selectedMasterServiceItem,
  ]);

  const getTimeSlots = (date?: Date, updateSelctedTimeSlot: boolean = true) => {
    const { serviceItemHid, careProviderHid, masterServiceItemHid } = formValues;
    if (isV2Mode(mode) && !masterServiceItemHid) return Promise.resolve(null);
    if (!isV2Mode(mode) && (!serviceItemHid || !careProviderHid)) return Promise.resolve(null);

    const fromDt = moment(date).startOf('month').toDate();
    const toDt = moment(date).endOf('month').toDate();
    const requestId = ++timeSlotsRequestIdRef.current;

    return new Promise<GetAvailableTimeSlotsRes | null>((resolve, reject) => {
      const onSuccess = (res: GetAvailableTimeSlotsRes) => {
        if (timeSlotsRequestIdRef.current !== requestId) {
          resolve(null);
          return;
        }
        setAvailableTimeSlots(res);
        if (updateSelctedTimeSlot) setSelectedTimeSlot(res);
        setMinDate(moment(res.minDate).toDate());
        setMaxDate(moment(res.maxDate).toDate());
        resolve(res);
      };
      const onError = (err: unknown) => {
        if (timeSlotsRequestIdRef.current !== requestId) {
          resolve(null);
          return;
        }
        setAvailableTimeSlots(null);
        if (updateSelctedTimeSlot) setSelectedTimeSlot(null);
        setMinDate(undefined);
        setMaxDate(undefined);
        reject(err);
      };

      if (isV2Mode(mode)) {
        getAvailableTimeSlotsV2(
          {
            masterServiceItemHid: masterServiceItemHid!,
            careProviderHids: requestedDetailCareProviderHid
              ? [requestedDetailCareProviderHid]
              : getEligibleCareProvidersV2(selectedMasterServiceItem).map((careProvider) => careProvider.value),
            fromDt,
            toDt,
          },
          onSuccess,
          onError,
        );
        return;
      }

      getAvailableTimeSlots(
        {
          serviceItemHid: serviceItemHid!,
          careProviderHid: careProviderHid!,
          fromDt,
          toDt,
        },
        onSuccess,
        onError,
      );
    });
  };

  const getTimeSlotsByCareProviders = (
    date?: Date,
    updateSelectedTimeSlot: boolean = true,
    careProviderHids?: string[],
  ) => {
    const { serviceItemHid } = formValues;
    if (!serviceItemHid) return Promise.resolve(null);

    const fromDt = moment(date).startOf('month').toDate();
    const toDt = moment(date).endOf('month').toDate();
    const requestId = ++timeSlotsRequestIdRef.current;

    return new Promise<GetAvailableTimeSlotsRes | null>((resolve, reject) => {
      if (!selectedServiceItem || !('careProviders' in selectedServiceItem)) {
        resolve(null);
        return;
      }
      getAvailableTimeSlotsByCareProviders(
        {
          serviceItemHid,
          careProviderHids:
            careProviderHids || selectedServiceItem.careProviders.map((careProvider) => careProvider.value),
          fromDt,
          toDt,
        },
        (res) => {
          if (timeSlotsRequestIdRef.current !== requestId) {
            resolve(null);
            return;
          }
          setAvailableTimeSlots(res);
          if (updateSelectedTimeSlot) setSelectedTimeSlot(res);
          setMinDate(moment(res.minDate).toDate());
          setMaxDate(moment(res.maxDate).toDate());
          resolve(res);
        },
        (err) => {
          if (timeSlotsRequestIdRef.current !== requestId) {
            resolve(null);
            return;
          }
          setAvailableTimeSlots(null);
          if (updateSelectedTimeSlot) setSelectedTimeSlot(null);
          setMinDate(undefined);
          setMaxDate(undefined);
          reject(err);
        },
      );
    });
  };

  useEffect(() => {
    const requestId = ++detailRequestIdRef.current;
    const isCurrentRequest = () => detailRequestIdRef.current === requestId;
    const cleanup = () => {
      if (detailRequestIdRef.current === requestId) detailRequestIdRef.current += 1;
    };

    if (!selectedDate) return cleanup;

    const { masterServiceItemHid } = formValues;

    if (isV2Mode(mode) && masterServiceItemHid) {
      setAvailableCareProviderTimeSlotsDetail(undefined);

      if (isSpecialtyAnyDoctor) {
        const eligibleCareProviders = getEligibleCareProvidersV2(selectedMasterServiceItem);
        setAvailableTimeSlotsDetail(undefined);
        setAvailableV2CareProviderTimeSlotsDetail(undefined);

        if (!eligibleCareProviders.length) {
          setAvailableV2CareProviderTimeSlotsDetail([]);
        } else {
          Promise.all(
            eligibleCareProviders.map(
              (careProvider) =>
                new Promise<CareProviderAvailableTimeslotV2 | null>((resolve) => {
                  getAvailableTimeSlotsDetailV2(
                    {
                      masterServiceItemHid,
                      careProviderHid: careProvider.value,
                      fromDt: moment(selectedDate).toDate(),
                    },
                    (res) => resolve({ careProvider, availableTimeslots: res.availableTimeslots }),
                    () => resolve(null),
                  );
                }),
            ),
          ).then((groups) => {
            if (isCurrentRequest()) {
              setAvailableV2CareProviderTimeSlotsDetail(
                groups.filter((group): group is CareProviderAvailableTimeslotV2 => !!group),
              );
            }
          });
        }
      } else {
        setAvailableV2CareProviderTimeSlotsDetail(undefined);
        if (requestedDetailCareProviderHid) {
          setAvailableTimeSlotsDetail(undefined);
          getAvailableTimeSlotsDetailV2(
            {
              masterServiceItemHid,
              careProviderHid: requestedDetailCareProviderHid,
              fromDt: moment(selectedDate).toDate(),
            },
            (res) => {
              if (isCurrentRequest()) setAvailableTimeSlotsDetail(res.availableTimeslots);
            },
            () => {
              if (isCurrentRequest()) setAvailableTimeSlotsDetail([]);
            },
          );
        }
      }

      if (!availableTimeSlots) getTimeSlots(selectedDate);
      return cleanup;
    }

    if (requestedDetailServiceItemHid && requestedDetailCareProviderHid && flow === FORM_FLOW.DEFAULT) {
      getAvailableTimeSlotsDetail(
        {
          serviceItemHid: requestedDetailServiceItemHid,
          careProviderHid: requestedDetailCareProviderHid,
          fromDt: moment(selectedDate).toDate(),
        },
        (res) => {
          if (isCurrentRequest()) setAvailableTimeSlotsDetail(res.availableTimeslots);
        },
        () => {
          if (isCurrentRequest()) setAvailableTimeSlotsDetail([]);
        },
      );
      if (!availableTimeSlots) getTimeSlots(selectedDate);
    } else if (requestedDetailServiceItemHid && flow === FORM_FLOW.DOCTOR_LAST) {
      if (!selectedServiceItem || !('careProviders' in selectedServiceItem)) return cleanup;

      getAvailableTimeSlotsDetailByCareProviders(
        {
          serviceItemHid: requestedDetailServiceItemHid,
          careProviderHids:
            filterCareProvider === 'All' || filterCareProvider === ''
              ? selectedServiceItem.careProviders.map((careProvider) => careProvider.value)
              : [filterCareProvider],
          fromDt: moment(selectedDate).toDate(),
        },
        (res) => {
          if (isCurrentRequest()) {
            setAvailableCareProviderTimeSlotsDetail(res.careProviderAvailableTimeslots);
          }
        },
        () => {
          if (isCurrentRequest()) setAvailableCareProviderTimeSlotsDetail([]);
        },
      );

      if (!availableTimeSlots) getTimeSlotsByCareProviders(selectedDate);
    }

    return cleanup;
  }, [
    formValues.masterServiceItemHid,
    isSpecialtyAnyDoctor,
    mode,
    requestedDetailCareProviderHid,
    requestedDetailServiceItemHid,
    selectedDate,
    selectedMasterServiceItem,
  ]);

  useEffect(() => {
    if (!selectedDate && formValues?.fromDt) setSelectedDate(resetDateToMidnight(formValues.fromDt));
  }, [formValues?.fromDt]);

  useEffect(() => {
    if (activeFormSection === FORM_SECTION.DATE_TIME && selectedSchedulingMinDate) {
      if (selectedTimeSlotsDetail && flow === FORM_FLOW.DEFAULT) setAvailableTimeSlotsDetail(selectedTimeSlotsDetail);
      if (selectedCareProviderTimeSlotsDetail && flow === FORM_FLOW.DOCTOR_LAST)
        setAvailableCareProviderTimeSlotsDetail(selectedCareProviderTimeSlotsDetail);
      if (selectedV2CareProviderTimeSlotsDetail && isSpecialtyAnyDoctor) {
        setAvailableV2CareProviderTimeSlotsDetail(selectedV2CareProviderTimeSlotsDetail);
      }
      setAvailableTimeSlots(selectedTimeSlot);

      const formDate = resetDateToMidnight(formValues.fromDt);

      const displayDate = formDate || moment(selectedSchedulingMinDate).toDate();

      setSelectedDate(formDate);

      const datePickerInstance = (datePickerRef.current as any)?.calendar?.instanceRef;
      if (datePickerInstance && displayDate) {
        !formDate && selectedTimeSlot && selectedTimeSlot.timeSlotDictionary
          ? datePickerInstance.changeMonthYear(Object.keys(selectedTimeSlot.timeSlotDictionary)[0])
          : datePickerInstance.changeMonthYear(displayDate);
      }

      const isSelectTimeslot = !!Object.keys(selectedTimeSlot?.timeSlotDictionary || {}).length;

      if (!isSelectTimeslot)
        (flow === FORM_FLOW.DOCTOR_LAST ? getTimeSlotsByCareProviders(displayDate) : getTimeSlots(displayDate)).then(
          (slots) => {
            if (slots && areAllTimeSlotsDisabledOrEmpty(slots.timeSlotDictionary)) {
              const slotMaxDate = new Date(slots.maxDate);
              if (
                datePickerInstance &&
                ((slotMaxDate.getFullYear() === displayDate.getFullYear() &&
                  slotMaxDate.getMonth() > displayDate.getMonth()) ||
                  slotMaxDate.getFullYear() > displayDate.getFullYear())
              ) {
                const nextMonthDate = moment(displayDate).add(1, 'month').startOf('month').toDate();
                datePickerInstance.changeMonthYear(nextMonthDate);
                flow === FORM_FLOW.DOCTOR_LAST
                  ? getTimeSlotsByCareProviders(nextMonthDate)
                  : getTimeSlots(nextMonthDate);
              }
            }
          },
        );
    }
  }, [
    activeFormSection,
    isSpecialtyAnyDoctor,
    selectedMasterServiceItem,
    selectedServiceItem,
    selectedV2CareProviderTimeSlotsDetail,
  ]);

  useEffect(() => {
    if (selectedSchedulingMinDate && selectedSchedulingMaxDate) {
      setMinDate(moment(selectedSchedulingMinDate).toDate());
      setMaxDate(moment(selectedSchedulingMaxDate).toDate());
    }
  }, [formValues.serviceItemHid, formValues.masterServiceItemHid]);

  useDidUpdateEffect(() => {
    if (isV2Mode(mode)) return;
    if (!selectedServiceItem || !('serviceItem' in selectedServiceItem)) return;

    setSelectedDate(undefined);
    setSelectedTimeSlot(undefined);
    setAvailableTimeSlots(undefined);
    setAvailableTimeSlotsDetail(undefined);
    setAvailableCareProviderTimeSlotsDetail(undefined);

    onChangeFormValues({
      ...formValues,
      careProviderHid: undefined,
      fromDt: undefined,
    });

    onSelectTime(undefined);

    const displayDate = moment(selectedServiceItem.serviceItem.minDate).toDate();
    const datePickerInstance = (datePickerRef.current as any)?.calendar?.instanceRef;

    datePickerInstance.changeMonthYear(displayDate);

    const careProviderHids =
      filterCareProvider === 'All' || filterCareProvider === ''
        ? selectedServiceItem.careProviders.map((careProvider) => careProvider.value)
        : [filterCareProvider];

    getTimeSlotsByCareProviders(displayDate, false, careProviderHids).then((slots) => {
      if (slots && areAllTimeSlotsDisabledOrEmpty(slots.timeSlotDictionary)) {
        const slotMaxDate = new Date(slots.maxDate);
        if (
          datePickerInstance &&
          ((slotMaxDate.getFullYear() === displayDate.getFullYear() &&
            slotMaxDate.getMonth() > displayDate.getMonth()) ||
            slotMaxDate.getFullYear() > displayDate.getFullYear())
        ) {
          const nextMonthDate = moment(displayDate).add(1, 'month').startOf('month').toDate();
          datePickerInstance.changeMonthYear(nextMonthDate);
          getTimeSlotsByCareProviders(nextMonthDate, false, careProviderHids);
        }
      }
    });
  }, [filterCareProvider]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const newWideIndexes: number[] = [];

      careProvideTimeSlotRefs.current.forEach((ele, index) => {
        if (!ele) return;

        const rect = ele.getBoundingClientRect();

        const boxRightEdge = rect.right;
        const boxTop = rect.top;
        const boxBottom = rect.bottom;

        const distanceX = boxRightEdge - e.clientX;
        const isNearRightEdge = distanceX < 12 && distanceX > 0;
        const isMouseInsideY = e.clientY >= boxTop && e.clientY <= boxBottom;

        if (isNearRightEdge && isMouseInsideY) newWideIndexes.push(index);
      });

      setScrollBarWideIndexes(newWideIndexes);
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  if (!mode) {
    const bookingModeOptions = [
      {
        value: BookingMode.Doctor,
        label: t('findSpecialist'),
      },
      {
        value: BookingMode.Specialty,
        label: t('findSpecialty'),
      },
      {
        value: BookingMode.EKMC,
        label: t('findGeneralOutpatientKowloonEast'),
      },
      {
        value: BookingMode.BH,
        label: t('findTwentyFourHourOutpatientKowloonTong'),
      },
    ];

    return (
      <div className={`${styles.container} ${isMobile ? styles.mobile : ''}`}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <StepIndicator value={1} total={3} />
            <span className={styles.title}>{t('onlineAppointmentService')}</span>
          </div>
          <Button
            type="secondary"
            onClick={() =>
              navigate(siteId ? `/ApptSysPublicWeb/edit?site=${siteId}` : '/ApptSysPublicWeb/edit', {
                state: { createBookingPath },
              })
            }
            className={styles.button}
          >
            {t('editOrCancelAppointment')}
          </Button>
        </div>

        <section className={styles.bookingModeSection}>
          <h1 className={styles.bookingModeTitle}>{t('selectBookingCategory')}</h1>
          <div className={styles.bookingModeOptions}>
            {bookingModeOptions.map((option) => {
              const siteCode = getBookingModeSiteCode(option.value);
              const addressTranslationKey = getSiteAddressTranslationKeyV2(siteCode);

              return (
                <Tooltip key={option.value} content={addressTranslationKey ? t(addressTranslationKey) : undefined}>
                  <button
                    type="button"
                    className={`${styles.bookingModeOption} ${getBookingModeOptionThemeClass(option.value)}`}
                    onClick={() => onSelectMode(option.value)}
                  >
                    <BookingModeIcon mode={option.value} />
                    <span>{option.label}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  const selectedBookingModeLabel =
    mode === BookingMode.Doctor
      ? t('specialist')
      : mode === BookingMode.Specialty
        ? t('specialty')
        : mode === BookingMode.EKMC
          ? t('generalOutpatientKowloonEast')
          : t('twentyFourHourOutpatientKowloonTong');

  return (
    <>
      <div className={`${styles.container} ${isMobile ? styles.mobile : ''}`}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <StepIndicator value={1} total={3} />
            <span className={styles.title}>{t('fillInInformation')}</span>
          </div>
          <Button type="secondary" onClick={handleBackToBookingMode} className={styles.button}>
            {t('backToBookingMode')}
          </Button>
        </div>
        <ControlledCollapsable isOpen={siteId.toUpperCase() === 'BH' && flow === FORM_FLOW.DOCTOR_LAST}>
          <div className={styles.hyperLinks}>
            <div
              className={styles.hyperLink}
              onClick={() => handleVideoClick('https://www.youtube.com/watch?v=rNLjZklkH8Y')}
            >
              <IconVideo />
              <a href="#" onClick={(e) => e.preventDefault()}>
                {t('webAppointmentGuidanceVideo')}
              </a>
            </div>
            {/* <div
            className={styles.hyperLink}
            onClick={() =>
              handleVideoClick("https://www.youtube.com/watch?v=o3pk4PmupPI")
            }
          >
            <IconVideo />
            <a href="#" onClick={(e) => e.preventDefault()}>
              {t("appAppointmentGuidanceVideo")}
            </a>
          </div>
          <div className={styles.hyperLink}>
            <IconDownloadApp />
            <a href="#" target="_blank" rel="noopener noreferrer">
              {t("downloadApp")}
            </a>
          </div> */}
          </div>
        </ControlledCollapsable>
        <VideoPopup isOpen={!!selectedVideoUrl} videoUrl={selectedVideoUrl || ''} onClose={handleCloseVideo} />

        <div className={styles.content}>
          <div className={styles.blocks}>
            <div className={styles.selectedBookingModeBlock}>
              <div className={styles.selectedBookingModeTitle}>
                <IconDone />
                <span>{t('bookingCategory')}</span>
              </div>
              <div className={styles.selectedBookingModeValue}>
                <span>{selectedBookingModeLabel}</span>
              </div>
            </div>
            {visibleFormOrder.map((section, index) => {
              const hasStartedBookingFlow =
                mode === BookingMode.Doctor
                  ? !!selectedDoctorOption
                  : mode === BookingMode.Specialty
                    ? !!selectedSpecialtyOption
                    : !!formValues.departmentValue;
              const title = hasStartedBookingFlow || index === 0 ? `${index + 1}. ${FORM_SECTION_LABELS[section]}` : '';
              const noScroll =
                section === FORM_SECTION.SPECIALTY
                  ? !formValues.specialtyOptionKey
                  : section === FORM_SECTION.DEPARTMENT
                    ? !formValues.departmentValue
                    : section === FORM_SECTION.PATIENT_FORM
                      ? patientFormNoScroll || Object.values(patientFormErrors).some((error) => error !== undefined)
                      : section === FORM_SECTION.SERVICE
                        ? flow === FORM_FLOW.DOCTOR_LAST
                        : section === FORM_SECTION.DATE_TIME
                          ? !!formValues.serviceItemHid && flow === FORM_FLOW.DOCTOR_LAST
                          : false;

              return (
                <CollapsableBlock
                  key={section}
                  title={title}
                  selectedLabels={getSelectedLabels(section)}
                  editable={getEditable(section)}
                  active={activeFormSection === section}
                  onEdit={() => handleSectionEdit(section)}
                  noScroll={noScroll}
                >
                  {section === FORM_SECTION.SPECIALTY ? (
                    aggregatedSpecialtyOptions && (
                      <div className={styles.serviceBlockContentContainer}>
                        {aggregatedSpecialtyOptions.length === 0 ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <div className={styles.selectorWithFilter}>
                            <InputField
                              name="specialtyNameFilter"
                              value={specialtyNameFilter}
                              onChange={(event) => setSpecialtyNameFilter(event.target.value)}
                              placeholder={t('filterSpecialtyName')}
                              searchIcon
                            />
                            {filteredAggregatedSpecialtyOptions?.length ? (
                              <VerticalSelector
                                onClick={(specialtyOptionKey) => {
                                  if (
                                    confirmedSectionEditRef.current !== FORM_SECTION.SPECIALTY &&
                                    (formValues.doctorPreference ||
                                      formValues.masterServiceItemHid ||
                                      formValues.fromDt)
                                  ) {
                                    setConfirmEditValue(specialtyOptionKey);
                                    setConfirmEditPopupSection(FORM_SECTION.SPECIALTY);
                                  } else {
                                    onChangeSpecialtyOptionKey(specialtyOptionKey);
                                  }
                                }}
                                options={filteredAggregatedSpecialtyOptions.map((option) => ({
                                  name: getLocalizedValueV2(option.specialty) || option.specialty.nameEn,
                                  value: option.key,
                                }))}
                                selectedValue={formValues.specialtyOptionKey}
                              />
                            ) : (
                              <span className={styles.noSearchResults}>{t('noMatchingSpecialties')}</span>
                            )}
                          </div>
                        )}
                        <div className={styles.appointmentMessage}>
                          {t('specialtyAppointmentMessage')}
                          <div className={styles.hospitalLink}>
                            <Trans i18nKey="specialtyHospitalLink">
                              <Link to={getAmcOnlineBookingUrl(i18n.language)}>HKBH Ambulatory Medical Centre</Link>
                            </Trans>
                          </div>
                        </div>
                      </div>
                    )
                  ) : section === FORM_SECTION.DEPARTMENT ? (
                    !isLegacyDepartmentLocked &&
                    (departmentOptions || departmentOptions === null) && (
                      <div className={styles.serviceBlockContentContainer}>
                        {departmentOptions.length === 0 || departmentOptions === null ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <VerticalSelector
                            onClick={(departmentValue) => {
                              const newFlow = departmentOptions?.find(
                                (opt) => opt.value === formValues.departmentValue,
                              )?.flow;
                              if (confirmedSectionEditRef.current === FORM_SECTION.DEPARTMENT) {
                                onChangeDepartmentValue(departmentValue);
                              } else if (
                                (newFlow === FORM_FLOW.DEFAULT && formValues.careProviderHid) ||
                                (flow === FORM_FLOW.DOCTOR_LAST && newFlow === FORM_FLOW.DEFAULT)
                              ) {
                                setConfirmEditValue(departmentValue);
                                setConfirmEditPopupSection(FORM_SECTION.DEPARTMENT);
                              } else if (
                                (newFlow === FORM_FLOW.DOCTOR_LAST && formValues.serviceItemHid) ||
                                (flow === FORM_FLOW.DEFAULT && newFlow === FORM_FLOW.DOCTOR_LAST)
                              ) {
                                setConfirmEditValue(departmentValue);
                                setConfirmEditPopupSection(FORM_SECTION.DEPARTMENT);
                              } else onChangeDepartmentValue(departmentValue);
                            }}
                            options={departmentOptions.map((opt) => ({
                              name: opt.name,
                              value: opt.value,
                            }))}
                            selectedValue={formValues.departmentValue}
                          />
                        )}
                        <div className={styles.appointmentMessage}>
                          {t('appointmentMessage')}
                          <div className={styles.hospitalLink}>
                            <Trans i18nKey={siteId.toUpperCase() === 'EKMC' ? 'ekmcHospitalLink' : 'hospitalLink'}>
                              <Link
                                to={`${
                                  siteId.toUpperCase() === 'EKMC'
                                    ? `https://www.hkbh.org.hk${
                                        i18n.language === 'zh-HK'
                                          ? ''
                                          : i18n.language === 'zh-CN'
                                            ? '/?lang=zh-hans'
                                            : '/?lang=en'
                                      }`
                                    : process.env.EKMC_URL ||
                                      `https://www.hkbh.org.hk/EKMC/${
                                        i18n.language === 'zh-HK' ? 'zh' : i18n.language === 'zh-CN' ? 'cn' : 'en'
                                      }/`
                                }`}
                              >
                                {siteId.toUpperCase() === 'EKMC'
                                  ? 'HKBH East Kowloon Medical Centre'
                                  : 'Hong Kong Baptist Hospital'}
                              </Link>
                              or
                              <Link to={getAmcOnlineBookingUrl(i18n.language)}>HKBH Ambulatory Medical Centre</Link>
                            </Trans>
                          </div>
                        </div>
                      </div>
                    )
                  ) : section === FORM_SECTION.DOCTOR ? (
                    doctorSelectionOptions !== undefined && (
                      <>
                        {(mode !== BookingMode.Specialty || isSpecialtyServiceFirst) &&
                        doctorSelectionOptions.length === 0 ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <div className={styles.selectorWithFilter}>
                            <InputField
                              name="doctorNameFilter"
                              value={doctorNameFilter}
                              onChange={(event) => setDoctorNameFilter(event.target.value)}
                              placeholder={t('filterDoctorName')}
                              searchIcon
                            />
                            {mode === BookingMode.Doctor ? (
                              filteredDoctorOptions?.length ? (
                                <VerticalSelector
                                  onClick={(doctorSpecialtyKey) => {
                                    if (
                                      confirmedSectionEditRef.current !== FORM_SECTION.DOCTOR &&
                                      (formValues.masterServiceItemHid || formValues.fromDt)
                                    ) {
                                      setConfirmEditValue(doctorSpecialtyKey);
                                      setConfirmEditPopupSection(FORM_SECTION.DOCTOR);
                                    } else onChangeCareProviderHid(doctorSpecialtyKey);
                                  }}
                                  options={filteredDoctorOptions.map((option) => ({
                                    name: getDoctorOptionLabel(option),
                                    value: option.key,
                                  }))}
                                  selectedValue={selectedDoctorOption?.key}
                                  infoOnClick={(doctorSpecialtyKey) => {
                                    const careProviderHid = doctorOptions?.find(
                                      (option) => option.key === doctorSpecialtyKey,
                                    )?.careProvider.value;
                                    setSelectedCareProviderHid(careProviderHid);
                                    setCareProviderInfoPopupIsOpen(!!careProviderHid);
                                  }}
                                />
                              ) : (
                                <span className={styles.noSearchResults}>
                                  {normalizeSearchValueV2(doctorNameFilter)
                                    ? t('noMatchingDoctors')
                                    : t('doctorSearchPrompt')}
                                </span>
                              )
                            ) : mode === BookingMode.Specialty ? (
                              <VerticalSelector
                                onClick={(careProviderHid) => {
                                  if (
                                    confirmedSectionEditRef.current !== FORM_SECTION.DOCTOR &&
                                    hasRelatedSelections(FORM_SECTION.DOCTOR)
                                  ) {
                                    setConfirmEditValue(careProviderHid);
                                    setConfirmEditPopupSection(FORM_SECTION.DOCTOR);
                                  } else {
                                    onChangeCareProviderHid(careProviderHid);
                                  }
                                }}
                                options={[
                                  {
                                    name: t('allDoctorsOrCareProviders'),
                                    value: SPECIALTY_ANY_DOCTOR_VALUE,
                                  },
                                  ...filteredSpecialtyDoctorOptions.map((option) => ({
                                    name: option.careProvider.name,
                                    value: option.careProvider.value,
                                  })),
                                ]}
                                selectedValue={
                                  formValues.doctorPreference === 'any'
                                    ? SPECIALTY_ANY_DOCTOR_VALUE
                                    : formValues.preferredCareProviderHid
                                }
                                infoOnClick={(hid) => {
                                  setSelectedCareProviderHid(hid);
                                  setCareProviderInfoPopupIsOpen(true);
                                }}
                                isInfoVisible={(hid) => hid !== SPECIALTY_ANY_DOCTOR_VALUE}
                              />
                            ) : filteredCareProviderOptions?.length ? (
                              <VerticalSelector
                                onClick={(careProviderHid) => {
                                  if (
                                    confirmedSectionEditRef.current !== FORM_SECTION.DOCTOR &&
                                    formValues.serviceItemHid
                                  ) {
                                    setConfirmEditValue(careProviderHid);
                                    setConfirmEditPopupSection(FORM_SECTION.DOCTOR);
                                  } else onChangeCareProviderHid(careProviderHid);
                                }}
                                options={filteredCareProviderOptions.map((opt) => ({
                                  name: opt.name,
                                  value: opt.value,
                                }))}
                                selectedValue={formValues.careProviderHid}
                                infoOnClick={(hid) => {
                                  setSelectedCareProviderHid(hid);
                                  setCareProviderInfoPopupIsOpen(true);
                                }}
                              />
                            ) : (
                              <span className={styles.noSearchResults}>{t('noMatchingDoctors')}</span>
                            )}
                          </div>
                        )}
                      </>
                    )
                  ) : section === FORM_SECTION.SERVICE ? (
                    (isV2Mode(mode) || serviceItemOptions || serviceItemOptions === null) && (
                      <>
                        {(
                          isV2Mode(mode)
                            ? masterServiceItemOptions.length === 0
                            : serviceItemOptions?.length === 0 || serviceItemOptions === null
                        ) ? (
                          <span className={styles.noOptions}>{t('noOptions')}</span>
                        ) : (
                          <div className={styles.selectorWithFilter}>
                            <InputField
                              name="serviceItemNameFilter"
                              value={serviceItemNameFilter}
                              onChange={(event) => setServiceItemNameFilter(event.target.value)}
                              title={t('filterServiceItemName')}
                              placeholder={t('filterServiceItemName')}
                            />
                            {isV2Mode(mode) ? (
                              filteredMasterServiceItemOptions.length ? (
                                <VerticalSelector
                                  onClick={(masterServiceItemHid) => {
                                    if (
                                      confirmedSectionEditRef.current !== FORM_SECTION.SERVICE &&
                                      hasRelatedSelections(FORM_SECTION.SERVICE)
                                    ) {
                                      setConfirmEditValue(masterServiceItemHid);
                                      setConfirmEditPopupSection(FORM_SECTION.SERVICE);
                                    } else {
                                      onChangeServiceItemHid(masterServiceItemHid);
                                      setFilterCareProvider('');
                                    }
                                  }}
                                  options={filteredMasterServiceItemOptions.map((option) => ({
                                    name:
                                      getLocalizedValueV2(option.masterServiceItem) || option.masterServiceItem.nameEn,
                                    value: option.masterServiceItem.hid,
                                  }))}
                                  selectedValue={formValues.masterServiceItemHid}
                                />
                              ) : (
                                <span className={styles.noSearchResults}>{t('noMatchingServiceItems')}</span>
                              )
                            ) : filteredServiceItemOptions?.length ? (
                              <VerticalSelector
                                onClick={(serviceItemHid) => {
                                  if (
                                    confirmedSectionEditRef.current !== FORM_SECTION.SERVICE &&
                                    formValues.fromDt &&
                                    formValues.toDt
                                  ) {
                                    setConfirmEditValue(serviceItemHid);
                                    setConfirmEditPopupSection(FORM_SECTION.SERVICE);
                                  } else {
                                    onChangeServiceItemHid(serviceItemHid);
                                    setFilterCareProvider('');
                                  }
                                }}
                                options={filteredServiceItemOptions.map((option) => {
                                  const serviceItem = getServiceItemFromOption(option);
                                  return {
                                    name: getLocalizedValue(serviceItem) || '',
                                    value: serviceItem.value,
                                  };
                                })}
                                selectedValue={formValues.serviceItemHid}
                              />
                            ) : (
                              <span className={styles.noSearchResults}>{t('noMatchingServiceItems')}</span>
                            )}
                          </div>
                        )}
                      </>
                    )
                  ) : section === FORM_SECTION.DATE_TIME ? (
                    <div className={styles.dateTimeContainer}>
                      {mode === BookingMode.Specialty && (
                        <div className={styles.dateTimeActions}>
                          <Button
                            type="secondary"
                            onClick={handleSelectSpecialtyDoctor}
                            className={styles.selectDoctorButton}
                          >
                            {t('noPreferenceDoctor')}
                          </Button>
                        </div>
                      )}
                      {isMobile &&
                        !isV2Mode(mode) &&
                        flow === FORM_FLOW.DOCTOR_LAST &&
                        selectedServiceItem &&
                        'careProviders' in selectedServiceItem && (
                          <Dropdown
                            clearable
                            placeholder={t('doctorOrCareProvider')}
                            options={[
                              {
                                name: t('allDoctorsOrCareProviders'),
                                value: 'All',
                              },
                              ...selectedServiceItem.careProviders,
                            ]}
                            value={filterCareProvider}
                            onChange={(careProvider) => setFilterCareProvider(careProvider)}
                            style={
                              !availableTimeSlots
                                ? {
                                    pointerEvents: 'none',
                                    opacity: 0.3,
                                  }
                                : {}
                            }
                            infoOnClick={(hid) => {
                              setSelectedCareProviderHid(hid);
                              setCareProviderInfoPopupIsOpen(true);
                            }}
                          />
                        )}
                      <div className={styles.legends}>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.amAvaliable}`}></div>
                          <span>{t('amAvailable')}</span>
                        </div>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.pmAvaliable}`}></div>
                          <span>{t('pmAvailable')}</span>
                        </div>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.amPmAvaliable}`}></div>
                          <span>{t('amPmAvailable')}</span>
                        </div>
                        <div className={styles.legend}>
                          <div className={`${styles.icon} ${styles.selectedDate}`}></div>
                          <span>{t('selectedDate')}</span>
                        </div>
                      </div>
                      <div
                        className={`${styles.dateTimePicker} ${
                          flow === FORM_FLOW.DOCTOR_LAST || isSpecialtyAnyDoctor ? styles.flowDoctorLast : ''
                        }`}
                      >
                        <div className={styles.datePickerWithReminder}>
                          <div className={styles.datePickerNoTimeSlotContainer}>
                            {noTimeSlot ? (
                              <div className={styles.noTimeSlot}>
                                {t(isFullyBooked ? 'allAppointmentSlotsFullyBooked' : 'noTimeSlot')}
                              </div>
                            ) : (
                              <div
                                className={styles.datePickerContainer}
                                style={
                                  isTimeSlotDetailLoading ? { opacity: 0.3, pointerEvents: 'none' } : { opacity: 1 }
                                }
                              >
                                <div className={styles.datePicker}>
                                  <DatePicker
                                    datePickerRef={datePickerRef}
                                    date={selectedDate}
                                    onMonthChange={(date) => {
                                      setAvailableTimeSlots(undefined);
                                      flow === FORM_FLOW.DOCTOR_LAST
                                        ? getTimeSlotsByCareProviders(
                                            date,
                                            false,
                                            filterCareProvider !== 'All' && filterCareProvider !== ''
                                              ? [filterCareProvider]
                                              : undefined,
                                          )
                                        : getTimeSlots(date, false);
                                    }}
                                    onChange={(date) => {
                                      if (isSpecialtyAnyDoctor) {
                                        setAvailableV2CareProviderTimeSlotsDetail(undefined);
                                      } else if (flow === FORM_FLOW.DOCTOR_LAST) {
                                        setAvailableCareProviderTimeSlotsDetail(undefined);
                                      } else {
                                        setAvailableTimeSlotsDetail(undefined);
                                      }
                                      if (formValues.fromDt || formValues.toDt) {
                                        setIsFormValuesDirty(true);
                                        onSelectTime(undefined);
                                        onChangeFormValues(
                                          isV2Mode(mode)
                                            ? {
                                                ...formValues,
                                                departmentValue: undefined,
                                                departmentHid: undefined,
                                                careProviderHid:
                                                  mode === BookingMode.Doctor
                                                    ? formValues.preferredCareProviderHid || formValues.careProviderHid
                                                    : undefined,
                                                serviceItemHid: undefined,
                                                fromDt: undefined,
                                                toDt: undefined,
                                              }
                                            : {
                                                ...formValues,
                                                careProviderHid:
                                                  flow === FORM_FLOW.DOCTOR_LAST
                                                    ? undefined
                                                    : formValues.careProviderHid,
                                                fromDt: undefined,
                                                toDt: undefined,
                                              },
                                        );
                                      }
                                      const timeSlotDate = Object.keys(
                                        availableTimeSlots?.timeSlotDictionary || {},
                                      ).find((dt) => moment(tzStartDay(dt)).isSame(date, 'date'));
                                      if (timeSlotDate) {
                                        setSelectedDate(moment(timeSlotDate).toDate());
                                      }
                                    }}
                                    filterDate={!minDate || !maxDate ? () => false : undefined}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    excludedDates={
                                      availableTimeSlots?.timeSlotDictionary
                                        ? Object.keys(availableTimeSlots.timeSlotDictionary).length > 0
                                          ? Object.keys(availableTimeSlots.timeSlotDictionary)
                                              .filter(
                                                (key) =>
                                                  availableTimeSlots.timeSlotDictionary![key].isDisabled ||
                                                  (availableTimeSlots.timeSlotDictionary![key].subGroups[0].roomSlot ===
                                                    0 &&
                                                    availableTimeSlots.timeSlotDictionary![key].subGroups[1]
                                                      .roomSlot === 0),
                                              )
                                              .map((date) => new Date(date))
                                          : []
                                        : []
                                    }
                                    dayClassName={(date) => {
                                      const timeSlotDate = Object.keys(
                                        availableTimeSlots?.timeSlotDictionary || {},
                                      ).find((dt) => moment(tzStartDay(dt)).isSame(date, 'date'));
                                      if (availableTimeSlots?.timeSlotDictionary && timeSlotDate) {
                                        const day = availableTimeSlots.timeSlotDictionary[timeSlotDate];

                                        const amGroup = day.subGroups.find((group) => group.name === 'AM');
                                        const pmGroup = day.subGroups.find((group) => group.name === 'PM');
                                        const amAvailable = amGroup && (amGroup.fulfillSlot || amGroup.roomSlot);
                                        const pmAvailable = pmGroup && (pmGroup.fulfillSlot || pmGroup.roomSlot);

                                        if (amAvailable && pmAvailable) return 'full';
                                        else if (amAvailable) return 'am';
                                        else if (pmAvailable) return 'pm';
                                        else return 'react-datepicker__day--disabled';
                                      }
                                      return 'react-datepicker__day--disabled';
                                    }}
                                    isLoading={availableTimeSlots === undefined}
                                    disable={availableTimeSlots === null}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          {departmentReminders.map((reminder) => (
                            <div key={reminder} className={styles.reminder}>
                              <IconReminder />
                              <span>{reminder}</span>
                            </div>
                          ))}
                        </div>
                        {!noTimeSlot &&
                          (isSpecialtyAnyDoctor ? (
                            isMobile ? (
                              <div
                                className={`${styles.careProviderTimeSlotsMobile} ${styles.v2CareProviderGroupsMobile}`}
                              >
                                {!selectedDate ? (
                                  <div className={styles.noTimeSlot}>{datePlaceholder}</div>
                                ) : availableV2CareProviderTimeSlotsDetail === undefined ? (
                                  <div className={styles.careProviderTimeSlotsMobileSkeleton}>
                                    {[0, 1, 2].map((index) => (
                                      <div key={index} className={styles.skeletonContainer}>
                                        <Skeleton width={96} height={20} />
                                        <Skeleton width={207} height={16} />
                                        <div className={styles.bottomBorder}></div>
                                      </div>
                                    ))}
                                  </div>
                                ) : v2CareProviderTimeSlotGroups?.length ? (
                                  v2CareProviderTimeSlotGroups.map((group) => {
                                    const earliestTimeslot = [...group.availableTimeslots].sort((left, right) =>
                                      left.fromDt.localeCompare(right.fromDt),
                                    )[0];
                                    const earliestTimeLabel =
                                      i18n.language === 'zh-CN'
                                        ? earliestTimeslot.labelSc
                                        : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                          ? earliestTimeslot.labelTc
                                          : earliestTimeslot.label;

                                    return (
                                      <CollapsableElement
                                        key={group.careProvider.value}
                                        className={styles.collapsableElementContainer}
                                        titleRenderer={(isExpanded) => (
                                          <div
                                            className={`${styles.title} ${styles.v2CareProviderMobileTitle} ${
                                              isExpanded ? styles.expanded : ''
                                            }`}
                                          >
                                            <div className={styles.leftSide}>
                                              <span className={styles.careProviderName}>{group.careProvider.name}</span>
                                              <div className={styles.text}>
                                                <span>{`${t('earliestAvailableTimeSlot')}: ${earliestTimeLabel}`}</span>
                                              </div>
                                            </div>
                                            <div className={styles.rightSide}>
                                              <img
                                                src={IconInfo}
                                                width={25}
                                                height={25}
                                                alt="info"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  setSelectedCareProviderHid(group.careProvider.value);
                                                  setCareProviderInfoPopupIsOpen(true);
                                                }}
                                              />
                                              <IconExpand
                                                className={styles.expandIcon}
                                                style={{ transform: isExpanded ? 'rotate(180deg)' : '' }}
                                              />
                                            </div>
                                          </div>
                                        )}
                                      >
                                        <div className={styles.v2CareProviderMobileTimes}>
                                          <TimePickerV2
                                            hideTitle
                                            selectedDate={selectedDate}
                                            selectedTime={formValues.fromDt}
                                            availableTimeslots={group.availableTimeslots}
                                            onSelectSlot={(timeSlot) =>
                                              onChangeTimeSlot(
                                                timeSlot.fromDt,
                                                timeSlot.toDt,
                                                group.careProvider.value,
                                                timeSlot,
                                              )
                                            }
                                            isSelected={(timeSlot) =>
                                              isAvailableTimeslotV2(timeSlot) &&
                                              formValues.careProviderHid === group.careProvider.value &&
                                              formValues.fromDt === timeSlot.fromDt &&
                                              formValues.toDt === timeSlot.toDt &&
                                              formValues.departmentHid === timeSlot.department.hid &&
                                              formValues.serviceItemHid === timeSlot.serviceItemHid
                                            }
                                          />
                                        </div>
                                      </CollapsableElement>
                                    );
                                  })
                                ) : (
                                  <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                                )}
                              </div>
                            ) : (
                              <div className={styles.v2CareProviderGroups}>
                                {!selectedDate ? (
                                  <div className={styles.noTimeSlot}>{datePlaceholder}</div>
                                ) : availableV2CareProviderTimeSlotsDetail === undefined ? (
                                  [0, 1, 2].map((index) => (
                                    <div key={index} className={styles.v2CareProviderGroup}>
                                      <Skeleton width={96} height={24} />
                                      <Skeleton width="100%" height={32} borderRadius={16} />
                                      <Skeleton width="100%" height={32} borderRadius={16} />
                                    </div>
                                  ))
                                ) : v2CareProviderTimeSlotGroups?.length ? (
                                  v2CareProviderTimeSlotGroups.map((group) => (
                                    <div key={group.careProvider.value} className={styles.v2CareProviderGroup}>
                                      <div className={styles.v2CareProviderGroupHeader}>
                                        <span>{group.careProvider.name}</span>
                                        <img
                                          src={IconInfo}
                                          width={25}
                                          height={25}
                                          alt="info"
                                          onClick={() => {
                                            setSelectedCareProviderHid(group.careProvider.value);
                                            setCareProviderInfoPopupIsOpen(true);
                                          }}
                                        />
                                      </div>
                                      <TimePickerV2
                                        hideTitle
                                        selectedDate={selectedDate}
                                        selectedTime={formValues.fromDt}
                                        availableTimeslots={group.availableTimeslots}
                                        onSelectSlot={(timeSlot) =>
                                          onChangeTimeSlot(
                                            timeSlot.fromDt,
                                            timeSlot.toDt,
                                            group.careProvider.value,
                                            timeSlot,
                                          )
                                        }
                                        isSelected={(timeSlot) =>
                                          isAvailableTimeslotV2(timeSlot) &&
                                          formValues.careProviderHid === group.careProvider.value &&
                                          formValues.fromDt === timeSlot.fromDt &&
                                          formValues.toDt === timeSlot.toDt &&
                                          formValues.departmentHid === timeSlot.department.hid &&
                                          formValues.serviceItemHid === timeSlot.serviceItemHid
                                        }
                                      />
                                    </div>
                                  ))
                                ) : (
                                  <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                                )}
                              </div>
                            )
                          ) : flow === FORM_FLOW.DEFAULT ? (
                            <div className={styles.timePicker}>
                              <TimePickerV2
                                selectedDate={selectedDate}
                                datePlaceholder={datePlaceholder}
                                selectedTime={formValues?.fromDt}
                                availableTimeslots={availableTimeSlotsDetail}
                                onSelect={isV2Mode(mode) ? undefined : onChangeTimeSlot}
                                onSelectSlot={
                                  isV2Mode(mode)
                                    ? (timeSlot) =>
                                        onChangeTimeSlot(
                                          timeSlot.fromDt,
                                          timeSlot.toDt,
                                          formValues.preferredCareProviderHid,
                                          timeSlot,
                                        )
                                    : undefined
                                }
                                isSelected={
                                  isV2Mode(mode)
                                    ? (timeSlot) =>
                                        isAvailableTimeslotV2(timeSlot) &&
                                        formValues.fromDt === timeSlot.fromDt &&
                                        formValues.toDt === timeSlot.toDt &&
                                        formValues.departmentHid === timeSlot.department.hid &&
                                        formValues.serviceItemHid === timeSlot.serviceItemHid
                                    : undefined
                                }
                              />
                            </div>
                          ) : flow === FORM_FLOW.DOCTOR_LAST ? (
                            isMobile ? (
                              <>
                                {selectedServiceItem && 'careProviders' in selectedServiceItem && selectedDate && (
                                  <div className={styles.careProviderTimeSlotsMobile}>
                                    {availableCareProviderTimeSlotsDetail ||
                                    (filterCareProvider && filterCareProvider !== 'All') ? (
                                      availableCareProviderTimeSlotsDetail &&
                                      availableCareProviderTimeSlotsDetail.filter(
                                        (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                      ).length > 1 &&
                                      (!filterCareProvider || filterCareProvider === 'All') ? (
                                        availableCareProviderTimeSlotsDetail
                                          .filter(
                                            (careProviderTimeslot) =>
                                              careProviderTimeslot.availableTimeslots.length > 0,
                                          )
                                          .map((careProviderTimeSlot) => (
                                            <CollapsableElement
                                              key={careProviderTimeSlot.careProviderHid}
                                              className={styles.collapsableElementContainer}
                                              titleRenderer={(isExpanded) => (
                                                <div className={styles.title}>
                                                  <div className={styles.leftSide}>
                                                    <span className={styles.careProviderName}>
                                                      {
                                                        selectedServiceItem.careProviders.find(
                                                          (opt) => opt.value === careProviderTimeSlot.careProviderHid,
                                                        )?.name
                                                      }
                                                    </span>
                                                    <div className={styles.text}>
                                                      <span>{`${t('earliestAvailableTimeSlot')}: ${
                                                        i18n.language === 'zh-CN'
                                                          ? careProviderTimeSlot.availableTimeslots[0].labelSc
                                                          : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                            ? careProviderTimeSlot.availableTimeslots[0].labelTc
                                                            : careProviderTimeSlot.availableTimeslots[0].label
                                                      }`}</span>
                                                    </div>
                                                  </div>

                                                  <div className={styles.rightSide}>
                                                    <img
                                                      src={IconInfo}
                                                      width={25}
                                                      height={25}
                                                      alt="info"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedCareProviderHid(
                                                          careProviderTimeSlot.careProviderHid,
                                                        );
                                                        setCareProviderInfoPopupIsOpen(true);
                                                      }}
                                                    />
                                                    <IconExpand
                                                      className={styles.expandIcon}
                                                      style={{
                                                        transform: isExpanded ? 'rotate(180deg)' : '',
                                                      }}
                                                    />
                                                  </div>
                                                </div>
                                              )}
                                            >
                                              <div className={styles.timeSlotMobile}>
                                                {careProviderTimeSlot.availableTimeslots.map((timeSlot, index) => {
                                                  return (
                                                    <button
                                                      className={styles.timeSlotLabelMobile}
                                                      key={index}
                                                      onClick={() => {
                                                        onChangeTimeSlot(
                                                          timeSlot.fromDt,
                                                          timeSlot.toDt,
                                                          careProviderTimeSlot.careProviderHid,
                                                        );
                                                      }}
                                                    >
                                                      {i18n.language === 'zh-CN'
                                                        ? timeSlot.labelSc
                                                        : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                          ? timeSlot.labelTc
                                                          : timeSlot.label}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            </CollapsableElement>
                                          ))
                                      ) : availableCareProviderTimeSlotsDetail &&
                                        availableCareProviderTimeSlotsDetail.filter(
                                          (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                        ).length === 0 ? (
                                        <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                                      ) : (
                                        <div
                                          className={`${styles.timePicker} ${styles.timePickerWithCareProviderNameMobile}`}
                                        >
                                          <div className={styles.careProviderNameContainer}>
                                            <span className={styles.careProviderName}>
                                              {selectedServiceItem.careProviders.find(
                                                (opt) => opt.value === filterCareProvider,
                                              )?.name ||
                                                selectedServiceItem.careProviders.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                                )?.name}
                                            </span>
                                            <img
                                              src={IconInfo}
                                              width={25}
                                              height={25}
                                              alt="info"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCareProviderHid(
                                                  filterCareProvider ||
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                                );
                                                setCareProviderInfoPopupIsOpen(true);
                                              }}
                                            />
                                          </div>
                                          <TimePickerV2
                                            selectedDate={selectedDate}
                                            selectedTime={formValues?.fromDt}
                                            availableTimeslots={
                                              availableCareProviderTimeSlotsDetail?.filter(
                                                (careProviderTimeslot) =>
                                                  careProviderTimeslot.availableTimeslots.length > 0,
                                              )[0].availableTimeslots
                                            }
                                            onSelect={(formDt, toDt) =>
                                              onChangeTimeSlot(
                                                formDt,
                                                toDt,
                                                filterCareProvider && filterCareProvider !== 'All'
                                                  ? filterCareProvider
                                                  : availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    ) : (
                                      <div className={styles.careProviderTimeSlotsMobileSkeleton}>
                                        <div className={styles.skeletonContainer}>
                                          <Skeleton width={84} height={20} />
                                          <Skeleton width={207} height={16} />
                                          <div className={styles.bottomBorder}></div>
                                        </div>
                                        <div className={styles.skeletonContainer}>
                                          <Skeleton width={70} height={20} />
                                          <Skeleton width={207} height={16} />
                                          <div className={styles.bottomBorder}></div>
                                        </div>
                                        <div className={styles.skeletonContainer}>
                                          <Skeleton width={84} height={20} />
                                          <Skeleton width={207} height={16} />
                                          <div className={styles.bottomBorder}></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div
                                  className={`${styles.careProviderDropdown} ${!availableTimeSlots && styles.disable}`}
                                >
                                  {selectedServiceItem && 'careProviders' in selectedServiceItem && (
                                    <Dropdown
                                      style={{ zIndex: 0 }}
                                      clearable
                                      type="blocks"
                                      placeholder={t('doctorOrCareProvider')}
                                      options={[
                                        {
                                          name: t('allDoctorsOrCareProviders'),
                                          value: 'All',
                                        },
                                        ...selectedServiceItem.careProviders,
                                      ]}
                                      value={filterCareProvider}
                                      onChange={(careProvider) => setFilterCareProvider(careProvider)}
                                      infoOnClick={(hid) => {
                                        setSelectedCareProviderHid(hid);
                                        setCareProviderInfoPopupIsOpen(true);
                                      }}
                                    />
                                  )}
                                </div>

                                {selectedServiceItem && 'careProviders' in selectedServiceItem && selectedDate && (
                                  <div className={styles.careProviderTimeSlots}>
                                    {availableCareProviderTimeSlotsDetail ||
                                    (filterCareProvider && filterCareProvider !== 'All') ? (
                                      availableCareProviderTimeSlotsDetail &&
                                      availableCareProviderTimeSlotsDetail.filter(
                                        (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                      ).length > 1 &&
                                      (!filterCareProvider || filterCareProvider === 'All') ? (
                                        (() => {
                                          const items = availableCareProviderTimeSlotsDetail.filter(
                                            (careProviderTimeslot) =>
                                              careProviderTimeslot.availableTimeslots.length > 0,
                                          );
                                          const length = items.length;
                                          const split = splitMap[length as 6 | 7 | 8 | 9];

                                          return split ? (
                                            <>
                                              <div className={styles.careProviderTimeSlots}>
                                                {items.slice(0, split[0]).map((item, index) => (
                                                  <div key={index} className={styles.careProviderTimeSlot}>
                                                    <div className={styles.careProviderName}>
                                                      <span>
                                                        {
                                                          selectedServiceItem.careProviders.find(
                                                            (opt) => opt.value === item.careProviderHid,
                                                          )?.name
                                                        }
                                                      </span>
                                                      <img
                                                        src={IconInfo}
                                                        width={25}
                                                        height={25}
                                                        alt="info"
                                                        onClick={() => {
                                                          setSelectedCareProviderHid(item.careProviderHid);
                                                          setCareProviderInfoPopupIsOpen(true);
                                                        }}
                                                      />
                                                    </div>

                                                    <div
                                                      ref={(el) => {
                                                        careProvideTimeSlotRefs.current[index] = el;
                                                      }}
                                                      className={`${styles.timeSlot} ${
                                                        scrollBarWideIndexes.includes(index) ? styles.moreWidth : ''
                                                      }`}
                                                    >
                                                      {item.availableTimeslots.map((timeSlot, timeSlotIndex) => (
                                                        <button
                                                          className={styles.timeSlotLabel}
                                                          key={timeSlotIndex}
                                                          onClick={() => {
                                                            onChangeTimeSlot(
                                                              timeSlot.fromDt,
                                                              timeSlot.toDt,
                                                              item.careProviderHid,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            {i18n.language === 'zh-CN'
                                                              ? timeSlot.labelSc
                                                              : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                                ? timeSlot.labelTc
                                                                : timeSlot.label}
                                                          </span>
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                              <div className={styles.careProviderTimeSlots}>
                                                {items.slice(split[0], split[0] + split[1]).map((item, index) => (
                                                  <div key={index} className={styles.careProviderTimeSlot}>
                                                    <div className={styles.careProviderName}>
                                                      <span>
                                                        {
                                                          selectedServiceItem.careProviders.find(
                                                            (opt) => opt.value === item.careProviderHid,
                                                          )?.name
                                                        }
                                                      </span>
                                                      <img
                                                        src={IconInfo}
                                                        width={25}
                                                        height={25}
                                                        alt="info"
                                                        onClick={() => {
                                                          setSelectedCareProviderHid(item.careProviderHid);
                                                          setCareProviderInfoPopupIsOpen(true);
                                                        }}
                                                      />
                                                    </div>

                                                    <div
                                                      ref={(el) => {
                                                        careProvideTimeSlotRefs.current[index] = el;
                                                      }}
                                                      className={`${styles.timeSlot} ${
                                                        scrollBarWideIndexes.includes(index) ? styles.moreWidth : ''
                                                      }`}
                                                    >
                                                      {item.availableTimeslots.map((timeSlot, timeSlotIndex) => (
                                                        <button
                                                          className={styles.timeSlotLabel}
                                                          key={timeSlotIndex}
                                                          onClick={() => {
                                                            onChangeTimeSlot(
                                                              timeSlot.fromDt,
                                                              timeSlot.toDt,
                                                              item.careProviderHid,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            {i18n.language === 'zh-CN'
                                                              ? timeSlot.labelSc
                                                              : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                                ? timeSlot.labelTc
                                                                : timeSlot.label}
                                                          </span>
                                                        </button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </>
                                          ) : (
                                            availableCareProviderTimeSlotsDetail
                                              .filter(
                                                (careProviderTimeslot) =>
                                                  careProviderTimeslot.availableTimeslots.length > 0,
                                              )
                                              .map((careProviderTimeSlot, index) => (
                                                <div key={index} className={styles.careProviderTimeSlot}>
                                                  <div className={styles.careProviderName}>
                                                    <span>
                                                      {
                                                        selectedServiceItem.careProviders.find(
                                                          (opt) => opt.value === careProviderTimeSlot.careProviderHid,
                                                        )?.name
                                                      }
                                                    </span>
                                                    <img
                                                      src={IconInfo}
                                                      width={25}
                                                      height={25}
                                                      alt="info"
                                                      onClick={() => {
                                                        setSelectedCareProviderHid(
                                                          careProviderTimeSlot.careProviderHid,
                                                        );
                                                        setCareProviderInfoPopupIsOpen(true);
                                                      }}
                                                    />
                                                  </div>

                                                  <div
                                                    ref={(el) => {
                                                      careProvideTimeSlotRefs.current[index] = el;
                                                    }}
                                                    className={`${styles.timeSlot} ${
                                                      scrollBarWideIndexes.includes(index) ? styles.moreWidth : ''
                                                    }`}
                                                  >
                                                    {careProviderTimeSlot.availableTimeslots.map(
                                                      (timeSlot, timeSlotIndex) => (
                                                        <button
                                                          className={styles.timeSlotLabel}
                                                          key={timeSlotIndex}
                                                          onClick={() => {
                                                            onChangeTimeSlot(
                                                              timeSlot.fromDt,
                                                              timeSlot.toDt,
                                                              careProviderTimeSlot.careProviderHid,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            {i18n.language === 'zh-CN'
                                                              ? timeSlot.labelSc
                                                              : i18n.language === 'zh-TW' || i18n.language === 'zh-HK'
                                                                ? timeSlot.labelTc
                                                                : timeSlot.label}
                                                          </span>
                                                        </button>
                                                      ),
                                                    )}
                                                  </div>
                                                </div>
                                              ))
                                          );
                                        })()
                                      ) : availableCareProviderTimeSlotsDetail &&
                                        availableCareProviderTimeSlotsDetail.filter(
                                          (careProviderTimeslot) => careProviderTimeslot.availableTimeslots.length > 0,
                                        ).length === 0 ? (
                                        <div className={styles.noTimeSlot}>{t('noTimeSlot')}</div>
                                      ) : (
                                        <div
                                          className={`${styles.timePicker} ${styles.timePickerWithCareProviderName}`}
                                        >
                                          <div className={styles.careProviderNameContainer}>
                                            <span className={styles.careProviderName}>
                                              {selectedServiceItem.careProviders.find(
                                                (opt) => opt.value === filterCareProvider,
                                              )?.name ||
                                                selectedServiceItem.careProviders.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )?.[0].careProviderHid,
                                                )?.name}
                                            </span>
                                            <img
                                              src={IconInfo}
                                              width={25}
                                              height={25}
                                              alt="info"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCareProviderHid(
                                                  filterCareProvider ||
                                                    availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                                );
                                                setCareProviderInfoPopupIsOpen(true);
                                              }}
                                            />
                                          </div>
                                          <TimePickerV2
                                            selectedDate={selectedDate}
                                            selectedTime={formValues?.fromDt}
                                            availableTimeslots={
                                              availableCareProviderTimeSlotsDetail?.filter(
                                                (careProviderTimeslot) =>
                                                  careProviderTimeslot.availableTimeslots.length > 0,
                                              )[0].availableTimeslots
                                            }
                                            onSelect={(formDt, toDt) =>
                                              onChangeTimeSlot(
                                                formDt,
                                                toDt,
                                                filterCareProvider && filterCareProvider !== 'All'
                                                  ? filterCareProvider
                                                  : availableCareProviderTimeSlotsDetail?.filter(
                                                      (careProviderTimeslot) =>
                                                        careProviderTimeslot.availableTimeslots.length > 0,
                                                    )[0].careProviderHid,
                                              )
                                            }
                                            twoColumns
                                          />
                                        </div>
                                      )
                                    ) : (
                                      <>
                                        <div className={styles.careProviderTimeSlot}>
                                          <div className={styles.careProviderName}>
                                            <Skeleton width={96} height={24} />
                                          </div>
                                          <div className={`${styles.timeSlot} ${styles.skeleton}`}>
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                          </div>
                                        </div>
                                        <div className={styles.careProviderTimeSlot}>
                                          <div className={styles.careProviderName}>
                                            <Skeleton width={80} height={24} />
                                          </div>
                                          <div className={`${styles.timeSlot} ${styles.skeleton}`}>
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                          </div>
                                        </div>
                                        <div className={styles.careProviderTimeSlot}>
                                          <div className={styles.careProviderName}>
                                            <Skeleton width={96} height={24} />
                                          </div>
                                          <div className={`${styles.timeSlot} ${styles.skeleton}`}>
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                            <Skeleton width="100%" height={20} />
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )
                          ) : null)}
                      </div>
                    </div>
                  ) : section === FORM_SECTION.PATIENT_FORM ? (
                    <PatientForm errors={patientFormErrors} formValues={formValues} onChange={handleChange} />
                  ) : null}
                </CollapsableBlock>
              );
            })}
          </div>
          <div className={styles.button}>
            <Button
              type="primary"
              disabled={!isAllRequiredFilled}
              onClick={() => {
                setSubmitted(true);
                if (validateAllFields()) onSubmit(availableTimeSlotsDetail!);
                // else {
                //   setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                // }
              }}
            >
              <span>{t('nextStepPreview')}</span>
            </Button>
          </div>
        </div>
      </div>
      <Popup
        title={t('confirmEdit')}
        isOpen={
          isBackToBookingModePopupOpen || !!pendingSectionEdit || (!!confirmEditValue && !!confirmEditPopupSection)
        }
        content={t('confirmEditDetail')}
        status={POPUP_STATUS.WARNING}
        confirmTitle={t('confirmAction')}
        closeTitle={t('cancel')}
        onConfirm={() => {
          if (isBackToBookingModePopupOpen) {
            setIsBackToBookingModePopupOpen(false);
            onBackToBookingMode();
            return;
          }

          if (pendingSectionEdit) {
            confirmedSectionEditRef.current = pendingSectionEdit;
            setActiveFormSection(pendingSectionEdit);
            setPendingSectionEdit(undefined);
            return;
          }

          switch (confirmEditPopupSection) {
            case FORM_SECTION.SPECIALTY:
              onChangeSpecialtyOptionKey(confirmEditValue);
              break;
            case FORM_SECTION.DEPARTMENT:
              onChangeDepartmentValue(confirmEditValue);
              break;
            case FORM_SECTION.DOCTOR:
              onChangeCareProviderHid(confirmEditValue);
              break;
            case FORM_SECTION.SERVICE:
              onChangeServiceItemHid(confirmEditValue);
              break;
          }
          setConfirmEditValue('');
          setConfirmEditPopupSection(undefined);
        }}
        onClose={() => {
          setIsBackToBookingModePopupOpen(false);
          setPendingSectionEdit(undefined);
          confirmedSectionEditRef.current = undefined;
          setConfirmEditValue('');
          setConfirmEditPopupSection(undefined);
        }}
      />
      {selectedCareProviderInfo && (
        <CareProviderInfoPopup
          isOpen={isCareProviderInfoPopupOpen}
          value={selectedCareProviderInfo}
          onClose={() => setCareProviderInfoPopupIsOpen(false)}
        />
      )}
    </>
  );
};

export default Form;
